import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Menu,
  OutlinedInput,
} from "@mui/material";
import {
  Download,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Refresh,
  LocalHospital,
  ReceiptLong,
  Savings,
  WbSunny,
  NightsStay,
  DonutLarge,
  Groups,
  Apartment,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import IncomeExpensesChart, { IncomeExpensesChartLegend } from "../../components/IncomeExpensesChart";
import IncomePerDepartmentChart from "../../components/IncomePerDepartmentChart";
import DonutLegendCard from "../../components/DonutLegendCard";
import RankedListCard from "../../components/RankedListCard";
import StatusTable from "../../components/StatusTable";
import { dashboardService } from "../../services/dashboardService";
import { getAllReports as getAllPathLabReports } from "../../services/pathLabTestReportService";
import { getRecentActivity } from "../../services/recentActivityService";
import { jsPDF } from "jspdf";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";
import { getOrganizationId } from "../../utils/organizationUtils";
import { filterByOrganization } from "../../utils/organizationUtils";
import subscriptionHistoryService from "../../services/subscriptionHistoryService";
import { packageIncludesPathModule } from "../../utils/attendanceFlow";
import { isPathModuleStaffRole } from "../../utils/pathRoleUtils";
import { fetchLowStockDashboardSummary } from "../../services/pharmacyLowStockService";
import { fetchPathLowStockDashboardSummary } from "../../services/pathLowStockService";
import { fetchQuickPatientSummary } from "../../services/patientPeriodSummaryService";
import {
  EMPTY_PATIENT_PERIOD_SUMMARY,
  formatMonthYearLabel,
} from "../../utils/patientPeriodSummary";
import {
  EMPTY_APPOINTMENT_STATUS_SUMMARY,
  clearAppointmentsListCache,
  fetchAppointmentStatusSummary,
  sumAppointmentStatusSummaries,
} from "../../utils/appointmentPeriodSummary";
import { getAllDepartment } from "../../services/departmentService";
import { filterDepartmentsForPackage } from "../../utils/subscriptionScopeUtils";

const normalizeDeptKey = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const NewDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userRoleNorm = String(user?.roleName || "").toLowerCase();
  const isPathUser = isPathModuleStaffRole(userRoleNorm);
  const isPharmacyUser = userRoleNorm === "pharmacy admin";
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([new Date().getMonth()]);
  const [selectedYears, setSelectedYears] = useState([new Date().getFullYear()]);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  // Primary selection used by existing single-period API callers
  const selectedMonth = selectedMonths[selectedMonths.length - 1] ?? new Date().getMonth();
  const selectedYear = selectedYears[selectedYears.length - 1] ?? new Date().getFullYear();

  const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const FY_MONTH_ORDER = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

  // Month names for dropdown
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];



  const [patientSummaryData, setPatientSummaryData] = useState({
    totalPatients: 0,
    newPatients: 0,
    oldPatients: 0,
    admittedPatients: 0,
    dischargedPatients: 0,
    totalChange: "0%",
    newChange: "0%",
    oldChange: "0%",
    totalTrend: "up",
    newTrend: "up",
    oldTrend: "up"
  });
  // FY YTD patient totals (Apr → latest selected month) for KPI dual-column layout
  const [patientYtdData, setPatientYtdData] = useState({
    totalPatients: 0,
    newPatients: 0,
    oldPatients: 0,
    admittedPatients: 0,
    dischargedPatients: 0,
  });

  const [incomeExpensesData, setIncomeExpensesData] = useState([]);
  const [incomeExpensesRawData, setIncomeExpensesRawData] = useState([]); // Store raw data in rupees for PDF
  const [incomePerDepartmentData, setIncomePerDepartmentData] = useState([]);

  // New state variables for additional tiles
  const [inventoryStatusData, setInventoryStatusData] = useState([]);
  const [pathInventoryStatusData, setPathInventoryStatusData] = useState([]);
  const [genderWisePatientsData, setGenderWisePatientsData] = useState([]);
  const [patientsPerDepartmentData, setPatientsPerDepartmentData] = useState([]);
  const [opdPeriodSummary, setOpdPeriodSummary] = useState(EMPTY_PATIENT_PERIOD_SUMMARY);
  const [ipdPeriodSummary, setIpdPeriodSummary] = useState({
    ...EMPTY_PATIENT_PERIOD_SUMMARY,
    admitted: 0,
    discharged: 0,
  });
  const [appointmentStatusSummary, setAppointmentStatusSummary] = useState({
    ...EMPTY_APPOINTMENT_STATUS_SUMMARY,
  });
  const [subscriptionPackage, setSubscriptionPackage] = useState("");
  const [allDepartments, setAllDepartments] = useState([]);
  const [availableDoctorsData, setAvailableDoctorsData] = useState([]);
  const [topTestsData, setTopTestsData] = useState([]);
  const [recentActivityData, setRecentActivityData] = useState([]);
  const dashboardRef = useRef(null);
  const selectedMonthRef = useRef(selectedMonth);
  const selectedYearRef = useRef(selectedYear);
  const hasLoadedDashboardRef = useRef(false);
  /** Cache Quick Patient Summary by `year-month` to avoid repeat fetches when toggling selection */
  const patientSummaryCacheRef = useRef(new Map());
  const appointmentSummaryCacheRef = useRef(new Map());

  const isPremiumPackage =
    String(subscriptionPackage || "").toUpperCase().includes("PREMIUM");
  // Path / Pathology modules are Intermediate + Premium only (not Basic)
  const hasPathPackageAccess = packageIncludesPathModule(subscriptionPackage);

  // Package-scoped departments: Basic/Intermediate → OPD (+ OPD+IPD); Premium → all
  const packageScopedDepartments = useMemo(
    () =>
      filterDepartmentsForPackage(allDepartments, {
        isPremium: isPremiumPackage,
      }),
    [allDepartments, isPremiumPackage]
  );

  const allowedDepartmentKeys = useMemo(() => {
    const names = new Set();
    const ids = new Set();
    packageScopedDepartments.forEach((dept) => {
      const name = normalizeDeptKey(dept?.name);
      if (name) names.add(name);
      if (dept?.id != null && dept.id !== "") ids.add(Number(dept.id));
    });
    return { names, ids };
  }, [packageScopedDepartments]);

  const isPackageDepartmentRow = useCallback(
    (item) => {
      // Until package + catalog resolve, keep existing rows (avoid empty flash)
      if (!subscriptionPackage || allDepartments.length === 0) return true;
      if (isPremiumPackage) return true;

      const id = item?.departmentId ?? item?.department_id ?? item?.deptId;
      if (id != null && id !== "" && allowedDepartmentKeys.ids.has(Number(id))) {
        return true;
      }

      const name = normalizeDeptKey(
        item?.department ||
          item?.departmentName ||
          item?.deptName ||
          item?.department_name
      );
      if (!name || /^other$/i.test(name)) return false;
      return allowedDepartmentKeys.names.has(name);
    },
    [
      subscriptionPackage,
      allDepartments.length,
      isPremiumPackage,
      allowedDepartmentKeys,
    ]
  );

  const scopedTotalDepartments = useMemo(() => {
    if (subscriptionPackage && allDepartments.length > 0) {
      return packageScopedDepartments.length;
    }
    return dashboardData?.totalDepartments ?? 0;
  }, [
    subscriptionPackage,
    allDepartments.length,
    packageScopedDepartments.length,
    dashboardData?.totalDepartments,
  ]);

  const scopedIncomePerDepartmentData = useMemo(() => {
    const raw = Array.isArray(incomePerDepartmentData) ? incomePerDepartmentData : [];
    const filtered = raw.filter(isPackageDepartmentRow);
    // Safety fallback: if package filter collapses dashboard to a single row, keep org data visible.
    if (raw.length > 1 && filtered.length <= 1) return raw;
    return filtered;
  }, [incomePerDepartmentData, isPackageDepartmentRow]);

  const scopedPatientsPerDepartmentData = useMemo(() => {
    const raw = Array.isArray(patientsPerDepartmentData) ? patientsPerDepartmentData : [];
    const filtered = raw.filter(isPackageDepartmentRow);
    if (raw.length > 1 && filtered.length <= 1) return raw;
    return filtered;
  }, [patientsPerDepartmentData, isPackageDepartmentRow]);

  const scopedGenderWisePatientsData = useMemo(() => {
    const raw = Array.isArray(genderWisePatientsData) ? genderWisePatientsData : [];
    const filtered = raw.filter(isPackageDepartmentRow);
    if (raw.length > 1 && filtered.length <= 1) return raw;
    return filtered;
  }, [genderWisePatientsData, isPackageDepartmentRow]);

  const scopedAvailableDoctorsData = useMemo(
    () =>
      Array.isArray(availableDoctorsData)
        ? availableDoctorsData.filter(isPackageDepartmentRow)
        : [],
    [availableDoctorsData, isPackageDepartmentRow]
  );

  // Department summary should favor full dashboard dataset so chart doesn't collapse to a single row.
  const departmentSummaryIncomeData = useMemo(() => {
    if (Array.isArray(incomePerDepartmentData) && incomePerDepartmentData.length > 0) {
      return incomePerDepartmentData;
    }
    return scopedIncomePerDepartmentData;
  }, [incomePerDepartmentData, scopedIncomePerDepartmentData]);

  const departmentSummaryPatientsData = useMemo(() => {
    if (Array.isArray(patientsPerDepartmentData) && patientsPerDepartmentData.length > 0) {
      return patientsPerDepartmentData;
    }
    return scopedPatientsPerDepartmentData;
  }, [patientsPerDepartmentData, scopedPatientsPerDepartmentData]);

  const departmentSummaryGenderData = useMemo(() => {
    if (Array.isArray(genderWisePatientsData) && genderWisePatientsData.length > 0) {
      return genderWisePatientsData;
    }
    return scopedGenderWisePatientsData;
  }, [genderWisePatientsData, scopedGenderWisePatientsData]);

  const selectionLabel = useMemo(() => {
    const monthPart =
      selectedMonths.length === 12
        ? "All months"
        : selectedMonths.length === 1
          ? monthNames[selectedMonths[0]]
          : `${selectedMonths.length} months`;
    const yearPart =
      selectedYears.length === 1 ? String(selectedYears[0]) : `${selectedYears.length} years`;
    return `${monthPart} ${yearPart}`;
  }, [selectedMonths, selectedYears, monthNames]);

  const greetingHour = new Date().getHours();
  const greeting =
    greetingHour < 12
      ? { text: "Good Morning", Icon: WbSunny }
      : greetingHour < 17
        ? { text: "Good Afternoon", Icon: WbSunny }
        : { text: "Good Evening", Icon: NightsStay };

  const periodSubtitle =
    selectedMonths.length === 1 && selectedYears.length === 1
      ? `${monthNames[selectedMonth]} ${selectedYear}`
      : selectionLabel;

  // Compact "Mon YYYY" label for the KPI cards' corner stat
  const kpiMonthLabel =
    selectedMonths.length === 1 && selectedYears.length === 1
      ? `${monthNamesShort[selectedMonth]} ${selectedYear}`
      : selectionLabel;

  const formatFinancialYearLabel = useCallback((year) => {
    const nextYearShort = String((Number(year) || 0) + 1).slice(-2);
    return `FY ${year}-${nextYearShort} (Apr-Mar)`;
  }, []);

  // Month/FY financial KPIs derived from income-expenses series so tiles follow selection
  const financialMetrics = useMemo(() => {
    const raw = Array.isArray(incomeExpensesRawData) ? incomeExpensesRawData : [];
    const months = selectedMonths.length ? selectedMonths : [selectedMonth];
    const selectedShort = new Set(months.map((m) => monthNamesShort[m]));

    const selectedRows = raw.filter((row) => selectedShort.has(String(row?.month || "")));
    const monthIncome = selectedRows.reduce((s, r) => s + (Number(r?.income) || 0), 0);
    const monthExpense = selectedRows.reduce((s, r) => s + (Number(r?.expenses) || 0), 0);
    const monthNet = monthIncome - monthExpense;

    // FY YTD: from Apr through latest selected month in FY order (or full FY if all months)
    let ytdMonths = FY_MONTH_ORDER;
    let latestFyIdx = FY_MONTH_ORDER.length - 1;
    if (months.length > 0 && months.length < 12) {
      latestFyIdx = Math.max(
        ...months.map((m) => FY_MONTH_ORDER.indexOf(monthNamesShort[m])).filter((i) => i >= 0),
        0
      );
      ytdMonths = FY_MONTH_ORDER.slice(0, latestFyIdx + 1);
    }
    const ytdRows = raw.filter((row) => ytdMonths.includes(String(row?.month || "")));
    const ytdIncome = ytdRows.reduce((s, r) => s + (Number(r?.income) || 0), 0);
    const ytdExpense = ytdRows.reduce((s, r) => s + (Number(r?.expenses) || 0), 0);
    const ytdNet = ytdIncome - ytdExpense;

    // MoM / prior-period trend vs month before earliest selected (FY order)
    const earliestFyIdx = Math.min(
      ...months.map((m) => FY_MONTH_ORDER.indexOf(monthNamesShort[m])).filter((i) => i >= 0),
      latestFyIdx
    );
    const priorIdx = earliestFyIdx > 0 ? earliestFyIdx - 1 : -1;
    const priorShort = priorIdx >= 0 ? FY_MONTH_ORDER[priorIdx] : null;
    const priorRow = priorShort
      ? raw.find((row) => String(row?.month || "") === priorShort)
      : null;
    const priorIncome = Number(priorRow?.income) || 0;
    const priorExpense = Number(priorRow?.expenses) || 0;
    const priorNet = priorIncome - priorExpense;

    const pctChange = (curr, prev) => {
      if (prev === 0) {
        if (curr === 0) return { change: "0%", trendUp: true };
        return { change: "100%", trendUp: curr > 0 };
      }
      const pct = Math.round(((curr - prev) / Math.abs(prev)) * 1000) / 10;
      return {
        change: `${pct > 0 ? "+" : ""}${pct}%`,
        trendUp: pct >= 0,
      };
    };
    const incomeTrend = pctChange(monthIncome, priorIncome);
    const expenseTrend = pctChange(monthExpense, priorExpense);
    const netTrend = pctChange(monthNet, priorNet);
    const priorMonthHint = priorShort ? ` vs ${priorShort}` : "";

    const days =
      months.reduce((sum, m) => {
        const yearForMonth = selectedYear;
        return sum + dayjs(`${yearForMonth}-${m + 1}-01`).daysInMonth();
      }, 0) || 1;

    const fmt = (n) => {
      const num = Number(n) || 0;
      const abs = Math.abs(num);
      if (abs >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
      if (abs >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
      return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    };

    return {
      monthIncome,
      monthExpense,
      monthNet,
      ytdIncome,
      ytdExpense,
      ytdNet,
      avgIncome: monthIncome / days,
      avgExpense: monthExpense / days,
      avgNet: monthNet / days,
      incomeTrend,
      expenseTrend,
      netTrend,
      priorMonthHint,
      ytdMonthIndices: ytdMonths
        .map((name) => monthNamesShort.indexOf(name))
        .filter((i) => i >= 0),
      fmt,
      days,
    };
  }, [
    incomeExpensesRawData,
    selectedMonths,
    selectedMonth,
    selectedYear,
    monthNamesShort,
    FY_MONTH_ORDER,
  ]);

  const isYearlyView = selectedMonths.length === 12;
  const monthSelectValue = isYearlyView ? "__all__" : selectedMonth;
  const yearSelectValue = selectedYear;

  const handleMonthsChange = (event) => {
    const value = event.target.value;
    if (value === "__all__") {
      setSelectedMonths([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);
      return;
    }
    const month = Number(value);
    setSelectedMonths(Number.isFinite(month) ? [month] : [new Date().getMonth()]);
  };

  const handleYearsChange = (event) => {
    const year = Number(event.target.value);
    setSelectedYears(Number.isFinite(year) ? [year] : [new Date().getFullYear()]);
  };

  const openDownloadMenu = (event) => setDownloadMenuAnchor(event.currentTarget);
  const closeDownloadMenu = () => setDownloadMenuAnchor(null);

  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);

  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);

  const isEqualArray = (a, b) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  };

  const handleDownloadPdf = async (mode = "selected") => {
    try {
      const pdf = new jsPDF("p", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Append plain text data for scrollable tiles so full datasets are captured
      const leftMargin = 36; // 0.5 inch
      const rightMargin = 36;
      const topMargin = 48;
      const bottomMargin = 48;
      let cursorY = topMargin;

      const addHeaderIfNeeded = (title) => {
        if (cursorY + 25 > pageHeight - bottomMargin) {
          pdf.addPage();
          cursorY = topMargin;
        }
        if (cursorY > topMargin) {
          cursorY += 8;
        }
        pdf.setDrawColor(220, 226, 235);
        pdf.setLineWidth(0.8);
        pdf.line(leftMargin, cursorY - 10, pageWidth - rightMargin, cursorY - 10);
        pdf.setFontSize(13);
        pdf.setFont("helvetica", "bold");
        pdf.text(title, leftMargin, cursorY);
        cursorY += 14;
      };

      const addKeyValueLine = (key, value) => {
        const maxWidth = pageWidth - leftMargin - rightMargin;
        const keyMaxWidth = 165;
        const valueX = leftMargin + keyMaxWidth + 14;
        const valueMaxWidth = maxWidth - keyMaxWidth - 14;
        const keyLabel = `${String(key)}:`;
        const keyWrapped = pdf.splitTextToSize(keyLabel, keyMaxWidth);
        const valueWrapped = pdf.splitTextToSize(value, valueMaxWidth);
        const maxLines = Math.max(keyWrapped.length, valueWrapped.length);

        for (let i = 0; i < maxLines; i++) {
          if (cursorY + 14 > pageHeight - bottomMargin) {
            pdf.addPage();
            cursorY = topMargin;
          }
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "bold");
          if (keyWrapped[i]) {
            pdf.text(keyWrapped[i], leftMargin, cursorY);
          }
          pdf.setFont("helvetica", "normal");
          if (valueWrapped[i]) {
            pdf.text(valueWrapped[i], valueX, cursorY);
          }
          cursorY += 12;
        }
      };

      const addDivider = () => {
        if (cursorY + 12 > pageHeight - bottomMargin) {
          pdf.addPage();
          cursorY = topMargin;
        }
        cursorY += 3; // Space before divider
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(leftMargin, cursorY, pageWidth - rightMargin, cursorY);
        cursorY += 8; // Space after divider
      };

      const formatCurrency = (n) => {
        let val = 0;
        if (n !== null && n !== undefined && n !== '') {
          if (typeof n === 'string') {
            val = parseFloat(n) || 0;
          } else {
            val = Number(n) || 0;
          }
        }
        return `Rs. ${val.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      };

      // Overview header
      const periodTitle =
        mode === "all"
          ? `Dashboard Data (Full FY ${selectedYear})`
          : `Dashboard Data (${selectionLabel})`;
      addHeaderIfNeeded(periodTitle);
      addKeyValueLine("Generated On", dayjs().format("DD MMM YYYY, hh:mm A"));
      addKeyValueLine("Download Mode", mode === "all" ? "All (Full Year)" : "Selected Period");
      addKeyValueLine("Months", selectedMonths.map((m) => monthNames[m]).join(", "));
      addKeyValueLine("Years", selectedYears.join(", "));
      addDivider();

      // First row stats
      addHeaderIfNeeded("1. Key Statistics");
      addKeyValueLine("Total Doctors", dashboardData?.totalDoctors ?? 0);
      addKeyValueLine("Total Staff", dashboardData?.totalStaff ?? 0);
      addKeyValueLine("Total Departments", scopedTotalDepartments);
      if (!isPathUser) {
        addKeyValueLine("Total Medicines", dashboardData?.totalMedicines ?? 0);
        addKeyValueLine("OOS Medicines", dashboardData?.oosMedicines ?? 0);
        addKeyValueLine("Expired Stocks", dashboardData?.expiredStocks ?? 0);
      }
      addKeyValueLine("Selected Income", formatCurrency(financialMetrics.monthIncome));
      addKeyValueLine("Selected Expenses", formatCurrency(financialMetrics.monthExpense));
      addKeyValueLine("Selected Net Income", formatCurrency(financialMetrics.monthNet));
      addKeyValueLine("FY YTD Income", formatCurrency(financialMetrics.ytdIncome));
      addKeyValueLine("FY YTD Expenses", formatCurrency(financialMetrics.ytdExpense));
      addKeyValueLine("FY YTD Net Income", formatCurrency(financialMetrics.ytdNet));
      addDivider();

      // Quick Patient Summary
      addHeaderIfNeeded("2. Quick Summary");
      addKeyValueLine("Total Patients", `${patientSummaryData?.totalPatients ?? 0} (${patientSummaryData?.totalTrend === "down" ? "-" : "+"}${patientSummaryData?.totalChange || "0%"})`);
      addKeyValueLine("New Patients", `${patientSummaryData?.newPatients ?? 0} (${patientSummaryData?.newTrend === "down" ? "-" : "+"}${patientSummaryData?.newChange || "0%"})`);
      addKeyValueLine("Returning Patients", `${patientSummaryData?.oldPatients ?? 0} (${patientSummaryData?.oldTrend === "down" ? "-" : "+"}${patientSummaryData?.oldChange || "0%"})`);
      addDivider();

      const periodLabel = formatMonthYearLabel(monthNames, selectedMonth, selectedYear);
      addHeaderIfNeeded(`3. Outpatient Summary (${periodLabel})`);
      addKeyValueLine("Total Patients", String(opdPeriodSummary?.total ?? 0));
      addKeyValueLine("New Patients", String(opdPeriodSummary?.new ?? 0));
      addKeyValueLine("Returning Patients", String(opdPeriodSummary?.returning ?? 0));
      (opdPeriodSummary?.byDepartment || [])
        .filter(isPackageDepartmentRow)
        .forEach((dept) => {
        addKeyValueLine(
          dept.department,
          `Total: ${dept.total}, New: ${dept.new}, Returning: ${dept.returning}`
        );
      });
      addDivider();

      if (isPremiumPackage) {
        addHeaderIfNeeded(`4. Inpatient Summary (${periodLabel})`);
        addKeyValueLine("Total Patients", String(ipdPeriodSummary?.total ?? 0));
        addKeyValueLine("New Patients", String(ipdPeriodSummary?.new ?? 0));
        addKeyValueLine("Returning Patients", String(ipdPeriodSummary?.returning ?? 0));
        (ipdPeriodSummary?.byDepartment || [])
          .filter(isPackageDepartmentRow)
          .forEach((dept) => {
          addKeyValueLine(
            dept.department,
            `Total: ${dept.total}, New: ${dept.new}, Returning: ${dept.returning}`
          );
        });
        addDivider();
      }

      // Income vs Expenses (April to March)
      addHeaderIfNeeded("5. Income vs Expenses (April–March)");
      // Use raw data in rupees for PDF (not the chart data which is in thousands)
      const useRawData = Array.isArray(incomeExpensesRawData) && incomeExpensesRawData.length > 0;
      const dataToUse = useRawData ? incomeExpensesRawData : incomeExpensesData; // Fallback to chart data if raw data not available
      
      if (Array.isArray(dataToUse) && dataToUse.length > 0) {
        dataToUse.forEach((m) => {
          // Use raw data which is already in rupees, or convert chart data if needed
          let incomeValue = typeof m?.income === 'number' ? m.income : parseFloat(m?.income || 0);
          let expensesValue = typeof m?.expenses === 'number' ? m.expenses : parseFloat(m?.expenses || 0);
          
          // If using chart data (not raw data), it's in thousands, so convert back to rupees
          // If using raw data, it's already in rupees, no conversion needed
          if (!useRawData) {
            // Chart data is in thousands, convert back to full rupees
            if (incomeValue > 0 && incomeValue < 1000) {
              incomeValue = incomeValue * 1000;
            }
            if (expensesValue > 0 && expensesValue < 1000) {
              expensesValue = expensesValue * 1000;
            }
          }
          
          const growthValue = parseFloat(m?.growth || 0);
          
          // Format as one compact line per month
          const monthName = m?.month || "-";
          const incomeStr = formatCurrency(incomeValue);
          const expensesStr = formatCurrency(expensesValue);
          const growthStr = `${growthValue.toFixed(2)}%`;
          
          // Check if we need a new page
          if (cursorY + 18 > pageHeight - bottomMargin) {
            pdf.addPage();
            cursorY = topMargin;
          }
          
          // Calculate column positions for compact layout
          const monthColWidth = 40; // Width for month name
          const incomeColWidth = 100; // Width for income
          const expensesColWidth = 100; // Width for expenses
          const growthColWidth = 80; // Width for growth
          
          const monthX = leftMargin;
          const incomeX = monthX + monthColWidth + 5;
          const expensesX = incomeX + incomeColWidth + 5;
          const growthX = expensesX + expensesColWidth + 5;
          
          pdf.setFontSize(10);
          pdf.setFont("helvetica", "bold");
          
          // Month name (left column)
          pdf.text(monthName, monthX, cursorY);
          
          // Income (second column)
          pdf.setFont("helvetica", "normal");
          const incomeText = `Inc: ${incomeStr}`;
          const incomeWrapped = pdf.splitTextToSize(incomeText, incomeColWidth);
          pdf.text(incomeWrapped[0], incomeX, cursorY);
          
          // Expenses (third column)
          const expensesText = `Exp: ${expensesStr}`;
          const expensesWrapped = pdf.splitTextToSize(expensesText, expensesColWidth);
          pdf.text(expensesWrapped[0], expensesX, cursorY);
          
          // Growth (fourth column)
          pdf.text(`Gr: ${growthStr}`, growthX, cursorY);
          
          // Move to next line for any wrapped text
          let maxWrappedLines = Math.max(incomeWrapped.length, expensesWrapped.length);
          if (maxWrappedLines > 1) {
            cursorY += 12; // Move down for wrapped text
            for (let i = 1; i < maxWrappedLines; i++) {
              if (cursorY + 14 > pageHeight - bottomMargin) {
                pdf.addPage();
                cursorY = topMargin;
              }
              if (incomeWrapped[i]) {
                pdf.text(incomeWrapped[i], incomeX, cursorY);
              }
              if (expensesWrapped[i]) {
                pdf.text(expensesWrapped[i], expensesX, cursorY);
              }
              cursorY += 12;
            }
          }
          
          // Add spacing after each month entry
          cursorY += 14;
        });
      } else {
        addKeyValueLine("Note", "No income/expenses data available");
      }
      addDivider();

      // Income per Department
      addHeaderIfNeeded(`6. Department Revenue (${monthNames[selectedMonth]} ${selectedYear})`);
      if (Array.isArray(scopedIncomePerDepartmentData) && scopedIncomePerDepartmentData.length > 0) {
        // Filter out departments with zero income and sort by income descending
        const sortedData = scopedIncomePerDepartmentData
          .filter(d => {
            const income = parseFloat(d?.income ?? d?.value ?? 0);
            return !isNaN(income) && income > 0;
          })
          .sort((a, b) => {
            const incomeA = parseFloat(a?.income ?? a?.value ?? 0);
            const incomeB = parseFloat(b?.income ?? b?.value ?? 0);
            return incomeB - incomeA;
          });
        
        if (sortedData.length > 0) {
          sortedData.forEach((d, index) => {
            // Try multiple field names for income
            const income = d?.income ?? d?.value ?? d?.incomeAmount ?? d?.totalIncome ?? 0;
            const incomeValue = typeof income === 'string' ? parseFloat(income) : (parseFloat(income) || 0);
            addKeyValueLine(`${index + 1}. ${d?.department || "Department"}`, formatCurrency(incomeValue));
          });
        } else {
          addKeyValueLine("Note", "No income data available for this month");
        }
      } else {
        addKeyValueLine("Note", "No income per department data");
      }
      addDivider();

      // Medicine Inventory Status (Top 5 OOS) — skip for pathology admin
      if (!isPathUser) {
        addHeaderIfNeeded("7. Medicine Inventory Status (Top 5 OOS)");
        if (Array.isArray(inventoryStatusData) && inventoryStatusData.length > 0) {
          inventoryStatusData.slice(0, 5).forEach((m, idx) => {
            addKeyValueLine(`#${idx + 1} ${m?.name || "-"}`, `Qty: ${m?.quantity ?? "-"}`);
            addKeyValueLine("Manufacturer", m?.manufacturer || "-");
            addKeyValueLine("Batch", m?.batchNumber || "-");
            addDivider();
          });
        } else {
          addKeyValueLine("Note", "No inventory data available");
          addDivider();
        }
      }

      // Top Tests — pathology only
      if (packageIncludesPathModule(subscriptionPackage)) {
        addHeaderIfNeeded("8. Top Tests");
        if (Array.isArray(topTestsData) && topTestsData.length > 0) {
          topTestsData.forEach((t, idx) => {
            addKeyValueLine(`${idx + 1}. ${t?.name || "Test"}`, `Count: ${t?.count ?? 0} (${(t?.pct ?? 0).toFixed(0)}%)`);
          });
          addDivider();
        } else {
          addKeyValueLine("Note", "No test order data available");
          addDivider();
        }
      }

      // Gender Wise Patients
      addHeaderIfNeeded(`9. Gender Wise Patients (${monthNames[selectedMonth]} ${selectedYear})`);
      if (Array.isArray(scopedGenderWisePatientsData) && scopedGenderWisePatientsData.length > 0) {
        scopedGenderWisePatientsData.forEach((d) => {
          addKeyValueLine(d?.department || "Department", `Male: ${d?.male ?? 0}, Female: ${d?.female ?? 0}, Total: ${d?.total ?? (Number(d?.male || 0) + Number(d?.female || 0))}`);
        });
        addDivider();
      } else {
        addKeyValueLine("Note", "No gender-wise patient data available");
        addDivider();
      }

      // Patients by Department
      addHeaderIfNeeded(`10. Patients by Department (${monthNames[selectedMonth]} ${selectedYear})`);
      if (Array.isArray(scopedPatientsPerDepartmentData) && scopedPatientsPerDepartmentData.length > 0) {
        scopedPatientsPerDepartmentData.forEach((d) => {
          addKeyValueLine(d?.department || "Department", `Patients: ${d?.patients ?? d?.count ?? 0}`);
        });
        addDivider();
      } else {
        addKeyValueLine("Note", "No patients per department data available");
        addDivider();
      }

      // Pathology Inventory Status
      if (showPathologyTile) {
        addHeaderIfNeeded("11. Pathology Inventory Status");
        if (Array.isArray(pathInventoryStatusData) && pathInventoryStatusData.length > 0) {
          pathInventoryStatusData.forEach((item) => {
            addKeyValueLine(item?.name || "Item", `Qty: ${item?.quantity ?? "-"}, Status: ${item?.status || (item?.quantity === 0 ? "OOS" : "Low")}`);
          });
          addDivider();
        } else {
          addKeyValueLine("Note", "No pathology inventory data available");
          addDivider();
        }
      }

      // Appointment Summary
      addHeaderIfNeeded(`12. Appointment Summary (${periodLabel})`);
      addKeyValueLine("Total Appointments", String(appointmentStatusSummary?.total ?? 0));
      addKeyValueLine("Completed", String(appointmentStatusSummary?.completed ?? 0));
      addKeyValueLine("Cancelled", String(appointmentStatusSummary?.cancelled ?? 0));
      addKeyValueLine("No Show", String(appointmentStatusSummary?.noShow ?? 0));
      addDivider();

      // Available Doctors
      addHeaderIfNeeded("13. Available Doctors (Today)");
      if (Array.isArray(scopedAvailableDoctorsData) && scopedAvailableDoctorsData.length > 0) {
        scopedAvailableDoctorsData.forEach((doc) => {
          const name = doc?.name ? `${doc.name}` : "Doctor";
          addKeyValueLine(name, `${doc?.department || "-"}`);
          addKeyValueLine("Visiting Hours", doc?.visitingHours || "9:00 AM - 5:00 PM");
          if (doc?.specialization) addKeyValueLine("Specialization", doc.specialization);
          addDivider();
        });
      } else {
        addKeyValueLine("Note", "No available doctors data");
        addDivider();
      }

      // Recent Activities
      addHeaderIfNeeded("14. Recent Activities");
      if (Array.isArray(recentActivityData) && recentActivityData.length > 0) {
        recentActivityData.forEach((event) => {
          addKeyValueLine(dayjs(event.timestamp).format("DD MMM, hh:mm A"), event.label);
        });
        addDivider();
      } else {
        addKeyValueLine("Note", "No recent activity data available");
        addDivider();
      }

      pdf.save(
        `hospital-dashboard-${mode}-FY${selectedYear}-M${selectedMonths.map((m) => m + 1).join("-")}.pdf`
      );
    } catch (e) {
      console.error("Failed to generate PDF", e);
    }
  };

  useEffect(() => {
    // Wait for user to be available
    if (!user) {
      console.log('[Dashboard] User not available yet, waiting...');
      return;
    }
    
    console.log('[Dashboard] useEffect triggered - user:', user?.roleName, 'organizationId check starting...');

    // PRIORITY: Check localStorage.organizationId FIRST (for super_admin who gets it set during login)
    // This ensures super_admin can fetch data immediately without waiting for AuthContext update
    let organizationId = null;
    const orgIdFromStorage = localStorage.getItem("organizationId");
    console.log(`[Dashboard] Checking localStorage.organizationId: ${orgIdFromStorage}`);
    if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
      organizationId = Number(orgIdFromStorage);
      console.log(`[Dashboard] ✅ Found organizationId in localStorage: ${organizationId}`);
    } else {
      // Fallback: Use getOrganizationId utility which checks user context and localStorage.user
      organizationId = getOrganizationId(user);
      if (organizationId) {
        console.log(`[Dashboard] ✅ Found organizationId from user context: ${organizationId}`);
      } else {
        console.log(`[Dashboard] ⚠️ No organizationId found in localStorage or user context`);
      }
    }
    
    // Update user object in localStorage if it doesn't have organizationId but we found it
    if (organizationId) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser.organizationId || parsedUser.organizationId !== organizationId) {
            const updatedUser = { ...parsedUser, organizationId: organizationId };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            console.log(`[Dashboard] Updated user object in localStorage with organizationId: ${organizationId}`);
            // Dispatch event to update AuthContext
            window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
              detail: { updatedUser } 
            }));
          }
        } catch (e) {
          console.error("[Dashboard] Error updating user object:", e);
        }
      }
    }
    
    const hasOrgId = organizationId != null && organizationId > 0;
    
    // Check if user is explicitly a base user (organizationId is null or undefined by design)
    // Base users should proceed immediately regardless of role
    const storedUser = localStorage.getItem("user");
    let isBaseUser = false;
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Base user has organizationId explicitly set to null, or undefined (not set)
        // Also check user object from context as fallback
        const userOrgId = user?.organizationId ?? parsedUser?.organizationId;
        isBaseUser = userOrgId === null || (userOrgId === undefined && !parsedUser?.hasOwnProperty('organizationId'));
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    // Also check user object from context directly
    if (!isBaseUser && user) {
      isBaseUser = user.organizationId === null || (user.organizationId === undefined && !user.hasOwnProperty('organizationId'));
    }
    
    // Function to fetch dashboard data
    const performFetch = () => {
      // Re-check organizationId right before fetching to ensure we have the latest value
      // For super_admin, also check localStorage.organizationId as fallback
      let currentOrgId = getOrganizationId(user);
      if (!currentOrgId) {
        const orgIdFromStorage = localStorage.getItem("organizationId");
        if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
          currentOrgId = Number(orgIdFromStorage);
          console.log(`[Dashboard] Using organizationId from localStorage fallback in performFetch: ${currentOrgId}`);
        }
      }
      console.log(`[Dashboard] 🚀 PERFORMING FETCH - organizationId: ${currentOrgId || 'none (base user)'}`);
      console.log(`[Dashboard] Calling fetchDashboardData, fetchInventoryStatusData, fetchAvailableDoctorsData`);
      fetchDashboardData().catch(err => {
        console.error('[Dashboard] ❌ Error in fetchDashboardData:', err);
      });
      fetchInventoryStatusData().catch(err => {
        console.error('[Dashboard] ❌ Error in fetchInventoryStatusData:', err);
      });
      fetchPathInventoryStatusData().catch(err => {
        console.error('[Dashboard] ❌ Error in fetchPathInventoryStatusData:', err);
      });
      fetchAvailableDoctorsData().catch(err => {
        console.error('[Dashboard] ❌ Error in fetchAvailableDoctorsData:', err);
      });
      fetchSubscriptionPackage().catch(err => {
        console.error('[Dashboard] ❌ Error in fetchSubscriptionPackage:', err);
      });
      fetchAllDepartments().catch(err => {
        console.error('[Dashboard] ❌ Error in fetchAllDepartments:', err);
      });
    };

    // For organization users (pathdesk, frontdesk, doctor, etc.), wait for organizationId
    // For base users (organizationId is null by design) or patients, proceed immediately
    const normalizedRole = (user?.roleName || "").toLowerCase();
    const isOrgUser = isPathModuleStaffRole(normalizedRole) ||
                      normalizedRole === 'frontdesk' || 
                      normalizedRole === 'doctor' || normalizedRole === 'surgeon' || 
                      normalizedRole === 'admin' || normalizedRole === 'superadmin' || normalizedRole === 'super_admin' || 
                      normalizedRole === 'pharmacy admin';
    
    // Base users should always proceed immediately, regardless of role
    if (isBaseUser) {
      console.log('[Dashboard] Base user detected (organizationId is null) - fetching data immediately');
      performFetch();
    } else if (hasOrgId) {
      // OrganizationId is available - fetch immediately
      console.log(`[Dashboard] ✅ OrganizationId available (${hasOrgId}), fetching data immediately`);
      performFetch();
    } else if (isOrgUser) {
      // Organization user but organizationId not yet available - check localStorage as fallback
      // For super_admin, organizationId should be in localStorage from login
      const orgIdFromStorage = localStorage.getItem("organizationId");
      if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
        console.log(`[Dashboard] ✅ OrganizationId found in localStorage (${orgIdFromStorage}), fetching data immediately`);
        performFetch();
      } else {
        console.log(`[Dashboard] ⚠️ Organization user (${user?.roleName}) but no organizationId found. hasOrgId: ${hasOrgId}, orgIdFromStorage: ${orgIdFromStorage}`);
        // Organization user but organizationId not yet available - wait for it
        console.log('[Dashboard] Organization user detected but organizationId not yet available, waiting...');
      
      let retryTimeoutId = null;
      
      const handleOrganizationIdUpdate = (event) => {
        const { updatedUser } = event.detail;
        const orgId = updatedUser?.organizationId || getOrganizationId(updatedUser);
        
        if (orgId && Number(orgId) > 0) {
          console.log(`[Dashboard] OrganizationId updated event received (${orgId}), fetching data immediately`);
          // Clear any pending timeouts since we got the event
          if (retryTimeoutId) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }
          // Small delay to ensure apiClient interceptor picks up the updated organizationId
          setTimeout(() => {
            performFetch();
          }, 100);
        }
      };

      window.addEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);

      // Set a timeout as fallback
      const timeout = setTimeout(() => {
        // For super_admin, also check localStorage.organizationId as fallback
        let recheckOrgId = getOrganizationId(user);
        if (!recheckOrgId) {
          const orgIdFromStorage = localStorage.getItem("organizationId");
          if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
            recheckOrgId = Number(orgIdFromStorage);
          }
        }
        if (recheckOrgId && recheckOrgId > 0) {
          console.log(`[Dashboard] OrganizationId now available (${recheckOrgId}), fetching data`);
          performFetch();
        } else {
          // Still no organizationId - try one more time after a bit
          console.log('[Dashboard] OrganizationId still not available, will retry once more...');
          retryTimeoutId = setTimeout(() => {
            // For super_admin, also check localStorage.organizationId as fallback
            let finalCheck = getOrganizationId(user);
            if (!finalCheck) {
              const orgIdFromStorage = localStorage.getItem("organizationId");
              if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
                finalCheck = Number(orgIdFromStorage);
              }
            }
            if (finalCheck && finalCheck > 0) {
              console.log(`[Dashboard] OrganizationId found on retry (${finalCheck}), fetching data`);
              performFetch();
            } else {
              console.warn('[Dashboard] OrganizationId not found after retries. apiClient will use localStorage value.');
              // Still fetch - apiClient will use whatever organizationId is in localStorage
              performFetch();
            }
            retryTimeoutId = null;
          }, 500);
        }
      }, 300); // Initial timeout reduced to 300ms

        return () => {
          clearTimeout(timeout);
          if (retryTimeoutId) {
            clearTimeout(retryTimeoutId);
          }
          window.removeEventListener('userOrganizationIdUpdated', handleOrganizationIdUpdate);
        };
      }
    } else {
      // Common user or patient - proceed immediately
      performFetch();
    }

    return undefined;
  }, [user, user?.organizationId]); // Re-run when user changes or organizationId is added

  // Consolidated fetch for period-dependent tiles (aggregates all selected months × years)
  useEffect(() => {
    if (!user) {
      return;
    }
    fetchIncomeExpensesData(selectedYear);
    fetchPeriodDependentAggregates();
  }, [selectedMonths, selectedYears, selectedMonth, selectedYear, user, user?.organizationId]);

  // Path inventory data only for Intermediate/Premium — clear or load when package resolves
  useEffect(() => {
    if (!subscriptionPackage) return;
    if (packageIncludesPathModule(subscriptionPackage)) {
      fetchPathInventoryStatusData().catch(() => {});
      fetchTopTestsData().catch(() => {});
    } else {
      setPathInventoryStatusData([]);
      setTopTestsData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionPackage]);

  // Recent Activities — org-wide, not period-scoped
  useEffect(() => {
    if (!user?.organizationId) return;
    getRecentActivity(5)
      .then(setRecentActivityData)
      .catch(() => setRecentActivityData([]));
  }, [user?.organizationId]);

  const fetchSubscriptionPackage = async () => {
    try {
      const organizationId = getOrganizationId(user);
      if (!organizationId) {
        setSubscriptionPackage("");
        return;
      }
      const active = await subscriptionHistoryService.getActiveSubscription(organizationId);
      const packageName =
        active?.subscriptionType?.subType ||
        active?.subscriptionType?.name ||
        active?.subscriptionType ||
        active?.packageName ||
        "";
      setSubscriptionPackage(String(packageName || "").toUpperCase());
    } catch (err) {
      console.warn("[Dashboard] Could not resolve subscription package:", err);
      setSubscriptionPackage("");
    }
  };

  const fetchAllDepartments = async () => {
    try {
      const data = await getAllDepartment();
      setAllDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("[Dashboard] Could not load departments for package scoping:", err);
      setAllDepartments([]);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Avoid blanking tiles on every refresh; only show "Refreshing..." for the first load
      if (!hasLoadedDashboardRef.current) {
        setLoading(true);
      }
      const statistics = await dashboardService.getDashboardStatistics();
      let oosMedicines = statistics?.oosMedicines ?? 0;
      let outOfStockCount = 0;
      let lowStockCount = 0;

      try {
        const organizationId = getOrganizationId(user);
        const summary = await fetchLowStockDashboardSummary(organizationId, 5);
        lowStockCount = summary.lowStockCount ?? 0;
        outOfStockCount = summary.outOfStockCount ?? 0;
        oosMedicines = summary.alertCount ?? lowStockCount;
      } catch (oosErr) {
        console.warn("[Dashboard] Client low-stock summary unavailable:", oosErr);
      }

      setDashboardData({
        ...statistics,
        oosMedicines,
        outOfStockCount,
        lowStockCount,
      });
      setError(null);
      hasLoadedDashboardRef.current = true;
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      // Don't set error state - just use fallback data so dashboard can still render
      // setError('Failed to fetch dashboard data');
      // Set fallback data
      setDashboardData((prev) => prev || {
        totalDoctors: 0,
        totalStaff: 0,
        totalDepartments: 0,
        totalMedicines: 0,
        totalIncome: 0,
        totalExpenses: 0,
        totalRevenue: 0,
        oosMedicines: 0,
        expiredStocks: 0,
      });
      hasLoadedDashboardRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeExpensesData = async (year) => {
    try {
      console.log(`Fetching income expenses data for year: ${year}`);
      const data = await dashboardService.getIncomeExpensesData(year);
      console.log('Raw income expenses data received:', JSON.stringify(data, null, 2));
      
      // Check if expenses are zero and try fallback
      if (data && data.monthlyData && Array.isArray(data.monthlyData)) {
        // Check if all expenses are zero
        const allExpensesZero = data.monthlyData.every(item => {
          const expenses = parseFloat(item.expenses || 0);
          return expenses === 0;
        });
        
        // Check if all values are zero and try income fallback
        const allZeros = data.monthlyData.every(item => {
          const income = parseFloat(item.income || 0);
          const expenses = parseFloat(item.expenses || 0);
          return income === 0 && expenses === 0;
        });
        
        if (allZeros && data.monthlyData.length > 0) {
          console.warn('⚠️ WARNING: Backend returned all zeros for income/expenses. Attempting fallback to aggregate from income-per-department data...');
          
          // Try to aggregate income from income-per-department for the selected month
          // Note: This uses the current selectedMonth from component state via ref
          try {
            const currentSelectedMonth = selectedMonthRef.current;
            const backendMonth = currentSelectedMonth + 1; // Convert 0-indexed to 1-indexed
            const deptData = await dashboardService.getIncomePerDepartmentData(backendMonth, year);
            
            if (deptData && deptData.departmentData && Array.isArray(deptData.departmentData)) {
              const totalIncome = deptData.departmentData.reduce((sum, dept) => {
                const income = parseFloat(dept.income || dept.value || 0);
                return sum + (isNaN(income) ? 0 : income);
              }, 0);
              
              if (totalIncome > 0) {
                // Map selected month to month name
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const currentMonthName = monthNames[currentSelectedMonth];
                
                // Update the corresponding month in monthlyData
                const monthIndex = data.monthlyData.findIndex(item => 
                  item.month === currentMonthName || 
                  item.month === (currentSelectedMonth + 1) ||
                  item.monthName === currentMonthName
                );
                
                if (monthIndex !== -1) {
                  // Ensure income is a number, not a string (BigDecimal serialization)
                  const incomeValue = typeof totalIncome === 'string' ? parseFloat(totalIncome) : totalIncome;
                  data.monthlyData[monthIndex].income = incomeValue;
                  
                  // Calculate growth - handle both cases: previous month > 0 and previous month = 0
                  // Financial year order: Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec, Jan, Feb, Mar
                  // So for Nov (index 7), previous month is Oct (index 6)
                  const prevMonthIndex = monthIndex > 0 ? monthIndex - 1 : data.monthlyData.length - 1;
                  const prevIncome = parseFloat(data.monthlyData[prevMonthIndex]?.income || 0);
                  
                  if (prevIncome > 0) {
                    // Normal growth calculation: ((current - previous) / previous) * 100
                    data.monthlyData[monthIndex].growth = ((incomeValue - prevIncome) / prevIncome) * 100;
                  } else if (incomeValue > 0 && prevIncome === 0) {
                    // Previous month had zero income, current month has income = 100% growth
                    data.monthlyData[monthIndex].growth = 100.0;
                  } else {
                    // Both are zero - keep existing growth value if any, otherwise 0
                    if (data.monthlyData[monthIndex].growth === undefined || data.monthlyData[monthIndex].growth === null) {
                      data.monthlyData[monthIndex].growth = 0;
                    }
                  }
                  
                  console.log(`✅ Fallback: Updated ${currentMonthName} (index ${monthIndex}) with income: ${incomeValue}, previous month income: ${prevIncome}, growth: ${data.monthlyData[monthIndex].growth}% (will be converted to ${(incomeValue / 1000).toFixed(2)}K for chart)`);
                } else {
                  // If month not found, try to add it
                  const incomeValue = typeof totalIncome === 'string' ? parseFloat(totalIncome) : totalIncome;
                  
                  // Calculate growth for the new month entry
                  let growthValue = 0;
                  if (data.monthlyData.length > 0) {
                    const lastMonthIndex = data.monthlyData.length - 1;
                    const prevIncome = parseFloat(data.monthlyData[lastMonthIndex].income || 0);
                    if (prevIncome > 0) {
                      growthValue = ((incomeValue - prevIncome) / prevIncome) * 100;
                    } else if (incomeValue > 0 && prevIncome === 0) {
                      growthValue = 100.0;
                    }
                  }
                  
                  data.monthlyData.push({
                    month: currentMonthName,
                    income: incomeValue,
                    expenses: 0,
                    growth: growthValue
                  });
                  console.log(`✅ Fallback: Added ${currentMonthName} with income: ${incomeValue}, growth: ${growthValue}% (will be converted to ${(incomeValue / 1000).toFixed(2)}K for chart)`);
                }
              } else {
                console.warn('Fallback: Income-per-department also returned zero income for selected month');
              }
            }
          } catch (fallbackError) {
            console.error('Failed to use income fallback aggregation:', fallbackError);
          }
        }
        
        // Note: Expenses are now calculated by the backend from:
        // - InvoicePurchase (supplier purchases)
        // - Invoice with type MEDICINE (medicine purchases)
        // - BillDetails (patient expenses: medicines, beds, rooms, lab tests, consumables, etc.)
        // - MedicineTransaction (medicines given to patients)
        // No frontend fallback needed for expenses
      }
      
      // Helper function to normalize month names
      const normalizeMonth = (monthInput) => {
        if (!monthInput) return null;
        const monthStr = monthInput.toString().trim();
        // Handle numeric months (1-12)
        const monthNum = parseInt(monthStr);
        if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
          const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          return monthNames[monthNum - 1].toLowerCase();
        }
        // Handle month names (full or abbreviated)
        return monthStr.substring(0, 3).toLowerCase();
      };
      
      // Helper function to extract numeric value from various field names
      const extractValue = (obj, fieldNames) => {
        for (const field of fieldNames) {
          const value = obj[field];
          if (value !== null && value !== undefined && value !== '') {
            const num = parseFloat(value);
            if (!isNaN(num)) return num;
          }
        }
        return null; // Return null instead of 0 to distinguish between missing and zero values
      };
      
      let monthlyData = null;
      
      // Handle different response formats
      if (Array.isArray(data)) {
        monthlyData = data;
        console.log('Data is array, using directly:', monthlyData);
      } else if (data && typeof data === 'object') {
        // Check for nested arrays
        monthlyData = data.monthlyData || data.data || data.monthly || data.incomeExpenses || data.monthlyIncomeExpenses || null;
        
        // If still not found, check if it's an object with month keys
        if (!monthlyData || !Array.isArray(monthlyData)) {
          const monthKeys = Object.keys(data).filter(key => 
            /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december|\d+)$/i.test(key)
          );
          if (monthKeys.length > 0) {
            monthlyData = monthKeys.map(key => ({
              month: key,
              ...data[key]
            }));
            console.log('Found month keys, created monthlyData:', monthlyData);
          }
        }
        
        // Check for separate income and expenses arrays
        if ((!monthlyData || !Array.isArray(monthlyData)) && (data.income || data.expenses)) {
          const incomeArr = Array.isArray(data.income) ? data.income : (data.income ? [data.income] : []);
          const expensesArr = Array.isArray(data.expenses) ? data.expenses : (data.expenses ? [data.expenses] : []);
          const maxLen = Math.max(incomeArr.length, expensesArr.length, 12); // Ensure at least 12 months
          monthlyData = Array.from({ length: maxLen }, (_, i) => ({
            month: i + 1, // Will be normalized later
            income: incomeArr[i] !== undefined ? incomeArr[i] : null,
            expenses: expensesArr[i] !== undefined ? expensesArr[i] : null,
            growth: data.growth?.[i] !== undefined ? data.growth[i] : null
          }));
          console.log('Created monthlyData from separate arrays:', monthlyData);
        }
        
        // Check if data itself is an object with month numbers as keys (1-12)
        if ((!monthlyData || !Array.isArray(monthlyData)) && Object.keys(data).some(k => /^\d+$/.test(k) && parseInt(k) >= 1 && parseInt(k) <= 12)) {
          monthlyData = Object.keys(data)
            .filter(k => /^\d+$/.test(k) && parseInt(k) >= 1 && parseInt(k) <= 12)
            .map(key => ({
              month: parseInt(key),
              ...data[key]
            }));
          console.log('Found numeric month keys, created monthlyData:', monthlyData);
        }
        
        // Last resort: if data is an object with direct income/expenses properties, try to create monthly data
        if ((!monthlyData || !Array.isArray(monthlyData)) && (data.income !== undefined || data.expenses !== undefined)) {
          // If income/expenses are single values, create a single month entry
          if (typeof data.income === 'number' || typeof data.expenses === 'number') {
            monthlyData = [{
              month: new Date().getMonth() + 1, // Current month as fallback
              income: data.income || 0,
              expenses: data.expenses || 0,
              growth: data.growth || 0
            }];
            console.log('Created single month entry from direct properties:', monthlyData);
          }
        }
      }
      
      if (Array.isArray(monthlyData) && monthlyData.length > 0) {
        console.log('Processing monthlyData array with', monthlyData.length, 'items');
        // Create a map for easier lookup (case-insensitive month matching)
        const monthMap = new Map();
        monthlyData.forEach((item, index) => {
          // Try to get month from various fields
          const monthKey = item.month || item.monthName || item.monthLabel || item.period || item.monthNumber || item.monthIndex || index + 1;
          const normalizedMonth = normalizeMonth(monthKey);
          
          if (normalizedMonth) {
            // Store with normalized key
            monthMap.set(normalizedMonth, item);
            // Also try to store with the original key if it's different
            if (monthKey && monthKey !== normalizedMonth) {
              const originalKey = monthKey.toString().toLowerCase().substring(0, 3);
              if (originalKey && !monthMap.has(originalKey)) {
                monthMap.set(originalKey, item);
              }
            }
          } else {
            // If month can't be determined, use index to map to month
            const monthNames = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
            if (index < 12) {
              monthMap.set(monthNames[index], item);
            }
          }
        });
        
        console.log('Month map created:', Array.from(monthMap.entries()));
        
        // Reorder months to start from April
        const orderedMonths = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
        
        // If monthMap is empty but we have monthlyData, log a warning
        if (monthMap.size === 0 && monthlyData.length > 0) {
          console.warn('Month map is empty, will try to use monthlyData in order');
        }
        
        const reorderedData = orderedMonths.map((monthName, index) => {
          const normalizedKey = monthName.toLowerCase();
          let monthData = monthMap.get(normalizedKey);
          
          // If monthMap lookup failed, try to use data by index
          if (!monthData && monthlyData[index]) {
            monthData = monthlyData[index];
            console.log(`Using monthlyData[${index}] for ${monthName}`);
          }
          
          // If still no data, try to find any data that might match
          if (!monthData && monthlyData.length > 0) {
            // Try to find data by checking all items
            const foundData = monthlyData.find(item => {
              const itemMonth = normalizeMonth(item.month || item.monthName || item.monthLabel || item.period || item.monthNumber);
              return itemMonth === normalizedKey;
            });
            if (foundData) {
              monthData = foundData;
            }
          }
          
          monthData = monthData || {};
          
          // Try multiple possible field names for income and expenses (expanded list)
          const incomeFieldNames = ['income', 'totalIncome', 'incomeAmount', 'amount', 'incomeValue', 'revenue', 'totalRevenue', 'incomeTotal', 'monthlyIncome', 'incomeData', 'incomeAmount', 'totalIncomeAmount'];
          const expensesFieldNames = ['expenses', 'totalExpenses', 'expensesAmount', 'expenseAmount', 'expensesValue', 'expenditure', 'totalExpenditure', 'expensesTotal', 'monthlyExpenses', 'expensesData', 'expenseAmount', 'totalExpensesAmount'];
          const growthFieldNames = ['growth', 'incomeGrowth', 'growthPercentage', 'growthValue', 'growthRate', 'percentageGrowth', 'monthlyGrowth', 'growthPercent'];
          
          let rawIncome = extractValue(monthData, incomeFieldNames);
          let rawExpenses = extractValue(monthData, expensesFieldNames);
          let rawGrowth = extractValue(monthData, growthFieldNames);
          
          // If still not found, try to find any numeric field that might be income/expenses
          if (rawIncome === null && Object.keys(monthData).length > 0) {
            // Look for fields that might contain income (case-insensitive)
            const incomeLikeFields = Object.keys(monthData).filter(k => 
              /income|revenue|earning/i.test(k) && (typeof monthData[k] === 'number' || (typeof monthData[k] === 'string' && !isNaN(parseFloat(monthData[k]))))
            );
            if (incomeLikeFields.length > 0) {
              rawIncome = parseFloat(monthData[incomeLikeFields[0]]) || null;
            }
          }
          
          if (rawExpenses === null && Object.keys(monthData).length > 0) {
            // Look for fields that might contain expenses (case-insensitive)
            const expensesLikeFields = Object.keys(monthData).filter(k => 
              /expense|expenditure|cost/i.test(k) && (typeof monthData[k] === 'number' || (typeof monthData[k] === 'string' && !isNaN(parseFloat(monthData[k]))))
            );
            if (expensesLikeFields.length > 0) {
              rawExpenses = parseFloat(monthData[expensesLikeFields[0]]) || null;
            }
          }
          
          console.log(`Month ${monthName}: rawIncome=${rawIncome}, rawExpenses=${rawExpenses}, rawGrowth=${rawGrowth}, monthData:`, monthData);
          
          // Convert to numbers - handle both number and string formats (BigDecimal from Java backend)
          // Use null check to preserve actual zero values
          let income = 0;
          let expenses = 0;
          let growth = 0;
          
          if (rawIncome !== null) {
            // Handle string numbers from BigDecimal serialization
            if (typeof rawIncome === 'string') {
              income = parseFloat(rawIncome) || 0;
            } else {
              income = parseFloat(rawIncome) || 0;
            }
          }
          
          if (rawExpenses !== null) {
            if (typeof rawExpenses === 'string') {
              expenses = parseFloat(rawExpenses) || 0;
            } else {
              expenses = parseFloat(rawExpenses) || 0;
            }
          }
          
          if (rawGrowth !== null) {
            if (typeof rawGrowth === 'string') {
              growth = parseFloat(rawGrowth) || 0;
            } else {
              growth = parseFloat(rawGrowth) || 0;
            }
          }
          
          // Store raw data in rupees (before conversion to thousands) for PDF generation
          // Note: income and expenses are already parsed and in rupees at this point
          const rawDataEntry = {
            month: monthName,
            income: income, // Already in rupees
            expenses: expenses, // Already in rupees
            growth: growth, // Already a percentage
          };
          
          // Check if values are already in thousands (if max value < 1000, assume already in K)
          // But also check if values are very small (< 1), which might indicate they're already in thousands
          const maxValue = Math.max(Math.abs(income), Math.abs(expenses));
          const needsConversion = maxValue >= 1000;
          
          // Convert to thousands for display (chart expects values in K)
          // Only convert if values are >= 1000 (likely in rupees), otherwise assume already in thousands
          const processedData = {
            month: monthName,
            income: needsConversion ? (income / 1000) : income,
            expenses: needsConversion ? (expenses / 1000) : expenses,
            growth: growth, // Growth is already a percentage
          };
          
          // Debug log for non-zero values
          if (income > 0 || expenses > 0) {
            console.log(`✅ Processed ${monthName}: income=${processedData.income}K, expenses=${processedData.expenses}K, growth=${processedData.growth}%`);
          }
          
          // Return both processed data for chart and raw data for PDF
          return { processedData, rawDataEntry };
        });
        
        // Separate processed data (for chart) and raw data (for PDF)
        const processedChartData = reorderedData.map(item => item.processedData);
        const rawDataInRupees = reorderedData.map(item => item.rawDataEntry);
        
        console.log('Final processed data:', processedChartData);
        console.log('Raw data in rupees for PDF:', rawDataInRupees);
        
        setIncomeExpensesRawData((prev) => (isEqualArray(prev, rawDataInRupees) ? prev : rawDataInRupees));
        setIncomeExpensesData((prev) => (isEqualArray(prev, processedChartData) ? prev : processedChartData));
      } else {
        console.warn('No monthlyData found or empty array. Raw data:', data);
        // Initialize with zeros for all months
        const orderedMonths = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
        const emptyData = orderedMonths.map(monthName => ({
          month: monthName,
          income: 0,
          expenses: 0,
          growth: 0,
        }));
        setIncomeExpensesData(emptyData);
        setIncomeExpensesRawData(emptyData); // Also set raw data
      }
    } catch (err) {
      console.error('Failed to fetch income expenses data:', err);
      // Initialize with zeros on error
      const orderedMonths = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
      const emptyData = orderedMonths.map(monthName => ({
        month: monthName,
        income: 0,
        expenses: 0,
        growth: 0,
      }));
      setIncomeExpensesData(emptyData);
      setIncomeExpensesRawData(emptyData); // Also set raw data
    }
  };

  const fetchIncomePerDepartmentData = async (month, year) => {
    try {
      const orgId = user?.organizationId;
      console.log(`[Dashboard] Fetching income per department - month: ${month}, year: ${year}, organizationId: ${orgId || 'none (common user)'}`);
      const data = await dashboardService.getIncomePerDepartmentData(month, year);
      console.log('[Dashboard] Received income per department data:', data);
      
      if (data.departmentData) {
        console.log(`[Dashboard] Department data count: ${data.departmentData.length}`);
        // Check if data has organizationId fields
        const hasOrgIdFields = data.departmentData.some(item => item?.organizationId || item?.organization_id);
        console.log(`[Dashboard] Data has organizationId fields: ${hasOrgIdFields}`);
        
        // Filter by organization as defensive measure (backend should already filter)
        const filteredData = filterByOrganization(data.departmentData, user);
        console.log(`[Dashboard] Filtered data count: ${filteredData.length}`);

        // Normalize fields for chart/PDF: real department name + usable income.
        // Backend sometimes sends name:"Other" (series bucket) while department has the real label.
        const palette = [
          "#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f",
          "#00838f", "#5d4037", "#455a64", "#c2185b", "#1565c0",
        ];
        const parseIncome = (raw) => {
          if (raw === null || raw === undefined || raw === "") return null;
          if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
          if (typeof raw === "object") {
            return parseIncome(raw.amount ?? raw.value ?? raw.income ?? raw.total);
          }
          const cleaned = String(raw).replace(/₹/g, "").replace(/,/g, "").replace(/\s/g, "").replace(/K$/i, "").trim();
          const num = Number(cleaned);
          return Number.isFinite(num) ? num : null;
        };
        const resolveIncome = (item) => {
          const candidates = [
            item?.income,
            item?.totalIncome,
            item?.incomeAmount,
            item?.amount,
            item?.revenue,
            item?.value,
          ];
          let foundZero = false;
          for (const candidate of candidates) {
            const parsed = parseIncome(candidate);
            if (parsed === null) continue;
            if (parsed > 0) return parsed;
            if (parsed === 0) foundZero = true;
          }
          return foundZero ? 0 : 0;
        };
        const resolveDepartment = (item, index) => {
          const fromDept = [
            item?.department,
            item?.departmentName,
            item?.deptName,
            item?.department_name,
            item?.label,
          ]
            .map((v) => String(v ?? "").trim())
            .find(Boolean);
          if (fromDept) return fromDept;
          const genericName = String(item?.name ?? "").trim();
          if (genericName && !/^other$/i.test(genericName)) return genericName;
          return `Department ${index + 1}`;
        };

        const normalizedData = filteredData.map((item, index) => {
          const department = resolveDepartment(item, index);
          const income = resolveIncome(item);
          return {
            ...item,
            department,
            name: department, // Recharts legend uses `name`
            income,
            color: item?.color || palette[index % palette.length],
          };
        });
        
        // Check if all income values are zero
        const allZeros = normalizedData.every(item => (item.income || 0) === 0);
        if (allZeros && normalizedData.length > 0) {
          console.warn(`[Dashboard] ⚠️ All income values are zero for organization ${orgId}. This might indicate:`);
          console.warn(`[Dashboard] 1. Organization-specific tables have no data yet`);
          console.warn(`[Dashboard] 2. Backend is returning base table data instead of organization data`);
        }
        
        setIncomePerDepartmentData((prev) => (isEqualArray(prev, normalizedData) ? prev : normalizedData));
      } else {
        console.log('[Dashboard] No department data found, setting empty array');
        setIncomePerDepartmentData((prev) => (prev.length ? [] : prev));
      }
    } catch (err) {
      console.error('[Dashboard] Failed to fetch income per department data:', err);
      setIncomePerDepartmentData((prev) => (prev.length ? [] : prev));
    }
  };

  /** Same counting rules as PatientsTable Excel Outpatient Summary (invoice-aligned OPD). */
  const fetchPatientSummaryData = async (month, year) => {
    try {
      const data = await fetchQuickPatientSummary(month, year);
      setPatientSummaryData({
        totalPatients: data.totalPatients ?? 0,
        newPatients: data.newPatients ?? 0,
        oldPatients: data.oldPatients ?? 0,
        admittedPatients: data.admittedPatients ?? data.ipdSummary?.admitted ?? 0,
        dischargedPatients: data.dischargedPatients ?? data.ipdSummary?.discharged ?? 0,
        totalChange: data.totalChange || "0%",
        newChange: data.newChange || "0%",
        oldChange: data.oldChange || "0%",
        totalTrend: data.totalTrend || "up",
        newTrend: data.newTrend || "up",
        oldTrend: data.oldTrend || "up",
      });
      // Reuse summaries already computed for Quick card — keep Outpatient/Inpatient panels in sync.
      setOpdPeriodSummary(data.opdSummary || EMPTY_PATIENT_PERIOD_SUMMARY);
      setIpdPeriodSummary(data.ipdSummary || { ...EMPTY_PATIENT_PERIOD_SUMMARY, admitted: 0, discharged: 0 });
    } catch (err) {
      console.error("[Dashboard] Failed to fetch patient summary (PatientsTable rules):", err);
      setPatientSummaryData({
        totalPatients: 0,
        newPatients: 0,
        oldPatients: 0,
        admittedPatients: 0,
        dischargedPatients: 0,
        totalChange: "0%",
        newChange: "0%",
        oldChange: "0%",
        totalTrend: "up",
        newTrend: "up",
        oldTrend: "up",
      });
      setOpdPeriodSummary(EMPTY_PATIENT_PERIOD_SUMMARY);
      setIpdPeriodSummary({ ...EMPTY_PATIENT_PERIOD_SUMMARY, admitted: 0, discharged: 0 });
    }
  };

  /** Build unique {month, year} periods from multi-select (month 0–11, calendar year). */
  const buildSelectedPeriods = () => {
    const months = selectedMonths.length ? selectedMonths : [selectedMonth];
    const years = selectedYears.length ? selectedYears : [selectedYear];
    const periods = [];
    const seen = new Set();
    years.forEach((year) => {
      months.forEach((month) => {
        const key = `${year}-${month}`;
        if (!seen.has(key)) {
          seen.add(key);
          periods.push({ month, year });
        }
      });
    });
    return periods;
  };

  /** FY YTD periods: Apr → latest selected month for each selected year (calendar year aligned). */
  const buildYtdPeriods = () => {
    const years = selectedYears.length ? selectedYears : [selectedYear];
    const months = selectedMonths.length ? selectedMonths : [selectedMonth];
    let latestFyIdx = FY_MONTH_ORDER.length - 1;
    if (months.length > 0 && months.length < 12) {
      latestFyIdx = Math.max(
        ...months.map((m) => FY_MONTH_ORDER.indexOf(monthNamesShort[m])).filter((i) => i >= 0),
        0
      );
    }
    const ytdShort = FY_MONTH_ORDER.slice(0, latestFyIdx + 1);
    const periods = [];
    const seen = new Set();
    years.forEach((year) => {
      ytdShort.forEach((shortName) => {
        const month = monthNamesShort.indexOf(shortName);
        if (month < 0) return;
        const key = `${year}-${month}`;
        if (!seen.has(key)) {
          seen.add(key);
          periods.push({ month, year });
        }
      });
    });
    return periods;
  };

  const sumPeriodSummaries = (list) => {
    const empty = { ...EMPTY_PATIENT_PERIOD_SUMMARY, byDepartment: [], admitted: 0, discharged: 0 };
    return (list || []).reduce(
      (acc, s) => ({
        total: (acc.total || 0) + (Number(s?.total) || 0),
        new: (acc.new || 0) + (Number(s?.new) || 0),
        returning: (acc.returning || 0) + (Number(s?.returning) || 0),
        admitted: (acc.admitted || 0) + (Number(s?.admitted) || 0),
        discharged: (acc.discharged || 0) + (Number(s?.discharged) || 0),
        byDepartment: acc.byDepartment,
      }),
      empty
    );
  };

  const getCachedAppointmentSummary = async (month, year) => {
    const key = `${year}-${month}`;
    const cache = appointmentSummaryCacheRef.current;
    if (cache.has(key)) return cache.get(key);
    try {
      const data = await fetchAppointmentStatusSummary(month, year);
      cache.set(key, data);
      return data;
    } catch (err) {
      console.error(`[Dashboard] Appointment summary failed for ${month}/${year}:`, err);
      return { ...EMPTY_APPOINTMENT_STATUS_SUMMARY };
    }
  };

  const getCachedPatientSummary = async (month, year) => {
    const key = `${year}-${month}`;
    const cache = patientSummaryCacheRef.current;
    if (cache.has(key)) return cache.get(key);
    try {
      const data = await fetchQuickPatientSummary(month, year);
      cache.set(key, data);
      return data;
    } catch (err) {
      console.error(`[Dashboard] Patient summary failed for ${month}/${year}:`, err);
      return null;
    }
  };

  /**
   * Aggregate patient / gender / dept / income-per-dept across all selected months.
   * Keeps single-month path identical; multi-month sums counts so tiles update with selection.
   */
  const fetchPeriodDependentAggregates = async () => {
    const periods = buildSelectedPeriods();
    const ytdPeriods = buildYtdPeriods();
    console.log("[Dashboard] Fetching period aggregates for", periods, "YTD", ytdPeriods);

    // Each aggregate below is independent of the others (different tiles/charts), so they run
    // concurrently instead of sequentially — sequential awaits here used to stagger each chart's
    // data arrival over several seconds, which read as the dashboard's donuts/bars "flickering"
    // in one after another as each fetch resolved.
    const patientSummaryPromise = (async () => {
    // Patient selected + YTD from cached periods (selected first for faster UI)
    try {
      const selectedResults = await Promise.all(
        periods.map(({ month, year }) => getCachedPatientSummary(month, year))
      );
      const selectedValid = selectedResults.filter(Boolean);
      if (selectedValid.length === 0) {
        setPatientSummaryData({
          totalPatients: 0,
          newPatients: 0,
          oldPatients: 0,
          admittedPatients: 0,
          dischargedPatients: 0,
          totalChange: "0%",
          newChange: "0%",
          oldChange: "0%",
          totalTrend: "up",
          newTrend: "up",
          oldTrend: "up",
        });
        setOpdPeriodSummary(EMPTY_PATIENT_PERIOD_SUMMARY);
        setIpdPeriodSummary({ ...EMPTY_PATIENT_PERIOD_SUMMARY, admitted: 0, discharged: 0 });
      } else {
        const totalPatients = selectedValid.reduce((s, d) => s + (Number(d.totalPatients) || 0), 0);
        const newPatients = selectedValid.reduce((s, d) => s + (Number(d.newPatients) || 0), 0);
        const oldPatients = selectedValid.reduce((s, d) => s + (Number(d.oldPatients) || 0), 0);
        const admittedPatients = selectedValid.reduce(
          (s, d) => s + (Number(d.admittedPatients ?? d.ipdSummary?.admitted) || 0),
          0
        );
        const dischargedPatients = selectedValid.reduce(
          (s, d) => s + (Number(d.dischargedPatients ?? d.ipdSummary?.discharged) || 0),
          0
        );
        const latest = selectedValid[selectedValid.length - 1];
        setPatientSummaryData({
          totalPatients,
          newPatients,
          oldPatients,
          admittedPatients,
          dischargedPatients,
          totalChange: latest.totalChange || "0%",
          newChange: latest.newChange || "0%",
          oldChange: latest.oldChange || "0%",
          totalTrend: latest.totalTrend || "up",
          newTrend: latest.newTrend || "up",
          oldTrend: latest.oldTrend || "up",
        });
        setOpdPeriodSummary(sumPeriodSummaries(selectedValid.map((d) => d.opdSummary)));
        setIpdPeriodSummary(sumPeriodSummaries(selectedValid.map((d) => d.ipdSummary)));
        // Optimistic YTD = selected until full YTD finishes
        setPatientYtdData({
          totalPatients,
          newPatients,
          oldPatients,
          admittedPatients,
          dischargedPatients,
        });
      }

      const selectedKeySet = new Set(periods.map((p) => `${p.year}-${p.month}`));
      const ytdExtra = ytdPeriods.filter((p) => !selectedKeySet.has(`${p.year}-${p.month}`));
      if (ytdExtra.length > 0) {
        await Promise.all(ytdExtra.map(({ month, year }) => getCachedPatientSummary(month, year)));
      }
      const ytdValid = (
        await Promise.all(ytdPeriods.map(({ month, year }) => getCachedPatientSummary(month, year)))
      ).filter(Boolean);
      setPatientYtdData({
        totalPatients: ytdValid.reduce((s, d) => s + (Number(d.totalPatients) || 0), 0),
        newPatients: ytdValid.reduce((s, d) => s + (Number(d.newPatients) || 0), 0),
        oldPatients: ytdValid.reduce((s, d) => s + (Number(d.oldPatients) || 0), 0),
        admittedPatients: ytdValid.reduce(
          (s, d) => s + (Number(d.admittedPatients ?? d.ipdSummary?.admitted) || 0),
          0
        ),
        dischargedPatients: ytdValid.reduce(
          (s, d) => s + (Number(d.dischargedPatients ?? d.ipdSummary?.discharged) || 0),
          0
        ),
      });
    } catch (err) {
      console.error("[Dashboard] Failed aggregated patient summary:", err);
      setPatientYtdData({
        totalPatients: 0,
        newPatients: 0,
        oldPatients: 0,
        admittedPatients: 0,
        dischargedPatients: 0,
      });
    }
    })();

    // The four aggregates below each feed one of the dashboard's donut/list "graph" tiles
    // (Appointment Summary, Gender Wise Patients, Patients by Department, Department Revenue).
    // Each used to call setState as soon as its own fetch resolved — since real network calls
    // never finish at exactly the same millisecond, that meant each tile popped from empty to
    // populated at a slightly different time, reading as the whole row "flickering" on load.
    // Instead, each block below RETURNS its computed value; all four setState calls are then
    // made together in one synchronous batch after every fetch has settled, so the tiles
    // change together in a single re-render.

    // Appointment status summary (Completed / Cancelled / No Show)
    const appointmentSummaryPromise = (async () => {
    try {
      const apptResults = await Promise.all(
        periods.map(({ month, year }) => getCachedAppointmentSummary(month, year))
      );
      return sumAppointmentStatusSummaries(apptResults);
    } catch (err) {
      console.error("[Dashboard] Failed aggregated appointment summary:", err);
      return { ...EMPTY_APPOINTMENT_STATUS_SUMMARY };
    }
    })();

    // Gender-wise: merge by department
    const genderWisePromise = (async () => {
    try {
      const genderLists = await Promise.all(
        periods.map(({ month, year }) =>
          dashboardService.getGenderWisePatientsPerDepartment(month, year)
            .then((data) => filterByOrganization(data?.genderData || [], user))
            .catch(() => [])
        )
      );
      const genderMap = new Map();
      genderLists.flat().forEach((row) => {
        const key = String(row?.department || row?.departmentName || "Unknown");
        const prev = genderMap.get(key) || { ...row, department: key, male: 0, female: 0, other: 0, total: 0 };
        prev.male = (Number(prev.male) || 0) + (Number(row.male) || 0);
        prev.female = (Number(prev.female) || 0) + (Number(row.female) || 0);
        prev.other = (Number(prev.other) || 0) + (Number(row.other) || 0);
        prev.total =
          (Number(prev.total) || 0) +
          (Number(row.total) || (Number(row.male) || 0) + (Number(row.female) || 0) + (Number(row.other) || 0));
        genderMap.set(key, prev);
      });
      return Array.from(genderMap.values());
    } catch (err) {
      console.error("[Dashboard] Failed aggregated gender data:", err);
      return [];
    }
    })();

    // Patients per department: merge by department
    const patientsPerDeptPromise = (async () => {
    try {
      const deptLists = await Promise.all(
        periods.map(({ month, year }) =>
          dashboardService.getPatientsPerDepartment(month, year)
            .then((data) => {
              const list = Array.isArray(data?.departmentData) ? data.departmentData : [];
              return filterByOrganization(
                list
                  .filter((dept) => dept && dept.department)
                  .map((dept) => {
                    const patientCount =
                      dept.patients !== undefined
                        ? dept.patients
                        : dept.count !== undefined
                          ? dept.count
                          : dept.patientCount !== undefined
                            ? dept.patientCount
                            : 0;
                    const count =
                      typeof patientCount === "string"
                        ? parseFloat(patientCount) || 0
                        : parseFloat(patientCount) || 0;
                    return {
                      department: dept.department,
                      patients: count,
                      count,
                      patientCount: count,
                    };
                  }),
                user
              );
            })
            .catch(() => [])
        )
      );
      const deptMap = new Map();
      deptLists.flat().forEach((row) => {
        const key = String(row.department);
        const prev = deptMap.get(key) || { department: key, patients: 0, count: 0, patientCount: 0 };
        const add = Number(row.patients) || 0;
        prev.patients += add;
        prev.count += add;
        prev.patientCount += add;
        deptMap.set(key, prev);
      });
      return Array.from(deptMap.values());
    } catch (err) {
      console.error("[Dashboard] Failed aggregated patients-per-dept:", err);
      return [];
    }
    })();

    // Income per department: merge by department (backend month is 1-indexed)
    const incomePerDeptPromise = (async () => {
    try {
      const incomeLists = await Promise.all(
        periods.map(({ month, year }) =>
          dashboardService
            .getIncomePerDepartmentData(month + 1, year)
            .then((data) => filterByOrganization(data?.departmentData || [], user))
            .catch(() => [])
        )
      );
      const palette = [
        "#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f",
        "#00838f", "#5d4037", "#455a64", "#c2185b", "#1565c0",
      ];
      const parseIncome = (raw) => {
        if (raw === null || raw === undefined || raw === "") return null;
        if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
        if (typeof raw === "object") {
          return parseIncome(raw.amount ?? raw.value ?? raw.income ?? raw.total);
        }
        const cleaned = String(raw).replace(/₹/g, "").replace(/,/g, "").replace(/\s/g, "").replace(/K$/i, "").trim();
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : null;
      };
      const resolveIncome = (item) => {
        const candidates = [
          item?.income,
          item?.totalIncome,
          item?.incomeAmount,
          item?.amount,
          item?.revenue,
          item?.value,
        ];
        let foundZero = false;
        for (const candidate of candidates) {
          const parsed = parseIncome(candidate);
          if (parsed === null) continue;
          if (parsed > 0) return parsed;
          if (parsed === 0) foundZero = true;
        }
        return foundZero ? 0 : 0;
      };
      const resolveDepartment = (item, index) => {
        const fromDept = [
          item?.department,
          item?.departmentName,
          item?.deptName,
          item?.department_name,
          item?.label,
        ]
          .map((v) => String(v ?? "").trim())
          .find(Boolean);
        if (fromDept) return fromDept;
        const genericName = String(item?.name ?? "").trim();
        if (genericName && !/^other$/i.test(genericName)) return genericName;
        return `Department ${index + 1}`;
      };

      const incomeMap = new Map();
      incomeLists.flat().forEach((item, index) => {
        const department = resolveDepartment(item, index);
        const income = resolveIncome(item);
        const prev = incomeMap.get(department) || {
          ...item,
          department,
          name: department,
          income: 0,
          color: item?.color || palette[incomeMap.size % palette.length],
        };
        prev.income = (Number(prev.income) || 0) + (Number(income) || 0);
        prev.department = department;
        prev.name = department;
        incomeMap.set(department, prev);
      });
      return Array.from(incomeMap.values());
    } catch (err) {
      console.error("[Dashboard] Failed aggregated income-per-dept:", err);
      return [];
    }
    })();

    const [, appointmentSummary, mergedGender, mergedDeptPatients, mergedDeptIncome] =
      await Promise.all([
        patientSummaryPromise,
        appointmentSummaryPromise,
        genderWisePromise,
        patientsPerDeptPromise,
        incomePerDeptPromise,
      ]);

    // Apply all four "graph tile" updates together in one batch so they change in a single
    // re-render instead of popping in one after another.
    setAppointmentStatusSummary(appointmentSummary);
    setGenderWisePatientsData((prev) => (isEqualArray(prev, mergedGender) ? prev : mergedGender));
    setPatientsPerDepartmentData((prev) =>
      isEqualArray(prev, mergedDeptPatients) ? prev : mergedDeptPatients
    );
    setIncomePerDepartmentData((prev) =>
      isEqualArray(prev, mergedDeptIncome) ? prev : mergedDeptIncome
    );
  };


  // New fetch functions for additional tiles
  const fetchInventoryStatusData = async () => {
    // Pathology admin does not use medicine inventory tiles/data
    if (isPathUser) {
      setInventoryStatusData([]);
      return;
    }
    try {
      const orgId = getOrganizationId(user);
      console.log(
        `[Dashboard] Fetching inventory status data - organizationId: ${orgId || "none (common user)"}`
      );

      let chartRows = [];
      try {
        const summary = await fetchLowStockDashboardSummary(orgId, 5);
        chartRows = summary.chartRows ?? [];
        console.log(
          `[Dashboard] Client medicine low-stock/OOS chart rows: ${chartRows.length}`
        );
      } catch (clientErr) {
        console.warn("[Dashboard] Client medicine inventory status failed:", clientErr);
      }

      if (chartRows.length === 0) {
        const data = await dashboardService.getTopOOSMedicines();
        console.log("[Dashboard] Received inventory status data:", data);

        if (data && data.topOOSMedicines) {
          const filteredData = filterByOrganization(data.topOOSMedicines, user);
          chartRows = filteredData.map((medicine, index) => ({
            id: medicine.id ?? index,
            name: medicine.name,
            quantity: medicine.quantity ?? 0,
            manufacturer: medicine.manufacturer || null,
            batchNumber: medicine.batchNumber || null,
            status:
              Number(medicine.quantity) === 0 ? "Out of stock" : "Low stock",
          }));
        }
      }

      setInventoryStatusData(chartRows);
    } catch (err) {
      console.error("[Dashboard] Failed to fetch inventory status data:", err);
      setInventoryStatusData([]);
    }
  };

  const fetchPathInventoryStatusData = async () => {
    // Basic package has no Path/Pathology module — skip path APIs/UI data
    if (!packageIncludesPathModule(subscriptionPackage)) {
      setPathInventoryStatusData([]);
      return;
    }
    try {
      const orgId =
        getOrganizationId(user) ||
        (() => {
          const stored = localStorage.getItem("organizationId");
          const num = stored != null ? Number(stored) : null;
          return num > 0 ? num : null;
        })();

      const summary = await fetchPathLowStockDashboardSummary(orgId, 5);

      setPathInventoryStatusData(summary.chartRows ?? []);
    } catch (err) {
      console.warn("[Dashboard] Failed to fetch path inventory status data:", err);
      setPathInventoryStatusData([]);
    }
  };

  const TOP_TESTS_COLORS = ["#db2777", "#2563eb", "#7c3aed", "#d97706", "#94a3b8"];

  // Aggregates raw lab report rows into "which tests were ordered how many
  // times" — no backend endpoint pre-computes this, so it's grouped client-side.
  const fetchTopTestsData = async () => {
    if (!packageIncludesPathModule(subscriptionPackage)) {
      setTopTestsData([]);
      return;
    }
    try {
      const reports = await getAllPathLabReports();
      const counts = new Map();
      (Array.isArray(reports) ? reports : []).forEach((report) => {
        const name = String(report?.testName ?? report?.test_name ?? "").trim();
        if (!name) return;
        counts.set(name, (counts.get(name) || 0) + 1);
      });

      const sorted = Array.from(counts.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      const top = sorted.slice(0, 4);
      const others = sorted.slice(4).reduce((sum, row) => sum + row.count, 0);
      const rows = others > 0 ? [...top, { name: "Others", count: others }] : top;

      const total = rows.reduce((sum, row) => sum + row.count, 0);
      setTopTestsData(
        rows.map((row, index) => ({
          ...row,
          color: TOP_TESTS_COLORS[index % TOP_TESTS_COLORS.length],
          pct: total > 0 ? (row.count / total) * 100 : 0,
        }))
      );
    } catch (err) {
      console.warn("[Dashboard] Failed to fetch top tests data:", err);
      setTopTestsData([]);
    }
  };

  const fetchGenderWisePatientsData = async (month, year) => {
    try {
      const orgId = user?.organizationId;
      console.log(`[Dashboard] Fetching gender-wise patients - month: ${month}, year: ${year}, organizationId: ${orgId || 'none (common user)'}`);
      const data = await dashboardService.getGenderWisePatientsPerDepartment(month, year);
      console.log('[Dashboard] Received gender-wise patients data:', data);
      
      if (data && data.genderData) {
        console.log(`[Dashboard] Gender data count: ${data.genderData.length}`);
        // Filter by organization as defensive measure (backend should already filter)
        const filteredData = filterByOrganization(data.genderData, user);
        console.log(`[Dashboard] Filtered gender data count: ${filteredData.length}`);
        if (filteredData.length === 0 && orgId) {
          console.warn(`[Dashboard] ⚠️ No gender-wise patient data for organization ${orgId}. Backend might be returning base table data.`);
        }
        setGenderWisePatientsData((prev) => (isEqualArray(prev, filteredData) ? prev : filteredData));
      } else {
        console.log('[Dashboard] No gender-wise patients data found, setting empty array');
        setGenderWisePatientsData((prev) => (prev.length ? [] : prev));
      }
    } catch (err) {
      console.error('[Dashboard] Failed to fetch gender-wise patients data:', err);
      setGenderWisePatientsData((prev) => (prev.length ? [] : prev));
    }
  };

  const fetchPatientsPerDepartmentData = async (month, year) => {
    try {
      console.log(`Fetching patients per department data for month: ${month}, year: ${year}`);
      const data = await dashboardService.getPatientsPerDepartment(month, year);
      console.log('Received patients per department data:', data);
      
      if (data && data.departmentData && Array.isArray(data.departmentData)) {
        // Ensure data is properly formatted and filter out invalid entries
        const processedData = data.departmentData
          .filter(dept => dept && dept.department) // Filter out null/undefined entries
          .map(dept => {
            // Normalize the patient count field - handle patients, count, or patientCount
            const patientCount = dept.patients !== undefined ? dept.patients : 
                                (dept.count !== undefined ? dept.count : 
                                (dept.patientCount !== undefined ? dept.patientCount : 0));
            
            // Ensure it's a number
            const count = typeof patientCount === 'string' ? parseFloat(patientCount) || 0 : (parseFloat(patientCount) || 0);
            
            return {
              department: dept.department,
              patients: count,
              count: count, // Keep both for compatibility
              patientCount: count // Also add patientCount for chart compatibility
            };
          });
        
        console.log('[Dashboard] Processed patients per department data:', processedData);
        // Filter by organization as defensive measure (backend should already filter)
        const filteredData = filterByOrganization(processedData, user);
        console.log(`[Dashboard] Filtered patients per department count: ${filteredData.length}`);
        const orgId = user?.organizationId;
        if (filteredData.length === 0 && orgId) {
          console.warn(`[Dashboard] ⚠️ No patients per department data for organization ${orgId}. Backend might be returning base table data.`);
        }
        setPatientsPerDepartmentData((prev) => (isEqualArray(prev, filteredData) ? prev : filteredData));
      } else {
        console.log('[Dashboard] No patients per department data found, setting empty array');
        setPatientsPerDepartmentData((prev) => (prev.length ? [] : prev));
      }
    } catch (err) {
      console.error('[Dashboard] Failed to fetch patients per department data:', err);
      setPatientsPerDepartmentData((prev) => (prev.length ? [] : prev));
    }
  };

  const fetchAvailableDoctorsData = async () => {
    try {
      const orgId = user?.organizationId;
      console.log(`[Dashboard] Fetching available doctors data - organizationId: ${orgId || 'none (common user)'}`);
      const data = await dashboardService.getAvailableDoctors();
      console.log('[Dashboard] Received available doctors data:', data);
      
      if (data && data.availableDoctors) {
        console.log(`[Dashboard] Available doctors count: ${data.availableDoctors.length}`);
        // Filter by organization as defensive measure (backend should already filter)
        const filteredData = filterByOrganization(data.availableDoctors, user);
        console.log(`[Dashboard] Filtered available doctors count: ${filteredData.length}`);
        if (filteredData.length === 0 && orgId) {
          console.warn(`[Dashboard] ⚠️ No available doctors data for organization ${orgId}. Backend might be returning base table data.`);
        }
        setAvailableDoctorsData(filteredData);
      } else {
        console.log('[Dashboard] No available doctors data found, setting empty array');
        setAvailableDoctorsData([]);
      }
    } catch (err) {
      console.error('Failed to fetch available doctors data:', err);
      setAvailableDoctorsData([]);
    }
  };

  const cardShadow = "0 1px 2px rgba(15, 23, 42, 0.05)";
  const cardBorder = "1px solid #e8edf3";
  const scrollSx = {
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
    flex: 1,
    "&::-webkit-scrollbar": { width: 5 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#cbd5e1",
      borderRadius: 8,
    },
  };

  // Compact KPI metric card — Selected Month | FY YTD layout (matches reference)
  // KPI tile — icon+label with headline = FY YTD, corner stat = selected
  // month, trend pill, optional sparkline (matches the approved mockup layout)
  const MetricCard = ({
    title,
    icon,
    color = "#2563eb",
    monthValue,
    ytdValue,
    monthLabel = "Selected Month",
    trend,
    trendUp,
    trendHint,
    sparklineData,
    footerLabel,
    footerValue,
    footerNote,
  }) => {
    const sparkPoints = useMemo(() => {
      if (!Array.isArray(sparklineData) || sparklineData.length < 2) return null;
      const values = sparklineData.map((v) => Number(v) || 0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min || 1;
      const stepX = 100 / (values.length - 1);
      return values
        .map((v, i) => `${(i * stepX).toFixed(1)},${(28 - ((v - min) / range) * 26).toFixed(1)}`)
        .join(" ");
    }, [sparklineData]);

    return (
      <Card
        sx={{
          height: "100%",
          background: "#fff",
          borderRadius: "10px",
          boxShadow: cardShadow,
          border: cardBorder,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            p: "6px 14px !important",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, minWidth: 0 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${color}14`,
                  color,
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(icon, { sx: { fontSize: 19 } })}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: "#64748b", lineHeight: 1.15 }}>
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.55rem",
                    lineHeight: 1.1,
                    color: "#0f172a",
                    wordBreak: "break-word",
                    mt: 0.1,
                  }}
                >
                  {ytdValue}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "right", flexShrink: 0, pl: 0.5 }}>
              <Typography sx={{ fontSize: "0.58rem", color: "#94a3b8", fontWeight: 700, whiteSpace: "nowrap" }}>
                {monthLabel}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#334155" }}>
                {monthValue}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 0.75, mt: 1.25 }}>
            <Box sx={{ minWidth: 0 }}>
              {trend != null ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  {trendUp ? (
                    <TrendingUp sx={{ fontSize: 14, color: "#16a34a" }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 14, color: "#dc2626" }} />
                  )}
                  <Typography
                    sx={{ fontSize: "0.74rem", fontWeight: 700, color: trendUp ? "#16a34a" : "#dc2626", whiteSpace: "nowrap" }}
                  >
                    {trend}
                  </Typography>
                </Box>
              ) : null}
              {trendHint ? (
                <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", fontWeight: 600 }}>
                  {trendHint}
                </Typography>
              ) : null}
              {footerLabel ? (
                <Typography sx={{ fontSize: "0.6rem", color: "#64748b", mt: trend != null ? 0.1 : 0 }}>
                  {footerLabel}{" "}
                  {footerValue != null ? (
                    <Box component="span" sx={{ fontWeight: 700, color: "#334155" }}>
                      {footerValue}
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
              {footerNote ? (
                <Typography sx={{ fontSize: "0.55rem", color: "#94a3b8" }}>{footerNote}</Typography>
              ) : null}
            </Box>
            {sparkPoints ? (
              <Box
                component="svg"
                viewBox="0 0 100 30"
                preserveAspectRatio="none"
                sx={{ width: 70, height: 25, flexShrink: 0 }}
              >
                <polyline
                  points={sparkPoints}
                  fill="none"
                  stroke={color}
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.85"
                />
              </Box>
            ) : null}
          </Box>
        </CardContent>
      </Card>
    );
  };


  // Quick Summary tile: New Patients/Follow-ups + org stats + medicine/test counts
  const QuickSummaryCard = ({ items = [] }) => (
    <Card
      sx={{
        height: "100%",
        background: "#fff",
        borderRadius: "10px",
        boxShadow: cardShadow,
        border: cardBorder,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ p: "6px 14px !important", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: "#64748b", mb: 0.5 }}>
          Quick Summary
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", rowGap: 0.35, columnGap: 1.25 }}>
          {items.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 0.5,
                minWidth: 0,
                px: 0.6,
                py: 0.3,
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: "transparent",
                ...(item.col ? { gridColumn: String(item.col) } : null),
              }}
            >
              <Typography sx={{ fontSize: "0.52rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: "0.76rem", color: item.color || "#0f172a", whiteSpace: "nowrap" }}>
                {item.value}
                {item.hint ? (
                  <Box component="span" sx={{ fontSize: "0.48rem", color: "#94a3b8", fontWeight: 600, ml: 0.35 }}>
                    {item.hint}
                  </Box>
                ) : null}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, subtitle, icon, iconColor = "#2563eb", children, height = "auto", scrollable = false, headerExtra = null }) => {
    const fillBody = height !== "auto";
    return (
    <Card
      sx={{
        height: height,
        minHeight: fillBody ? 0 : undefined,
        width: height === "100%" ? "100%" : undefined,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: cardShadow,
        border: cardBorder,
      }}
    >
      <CardContent
        sx={{
          p: "8px 10px !important",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {(title || headerExtra) && (
          <Box sx={{ mb: 0.45, flexShrink: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
                {icon ? (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "7px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${iconColor}14`,
                      color: iconColor,
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(icon, { sx: { fontSize: 14, ...(icon.props?.sx || {}) } })}
                  </Box>
                ) : null}
                {title ? (
                  <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#0f172a", lineHeight: 1.15 }}>
                    {title}
                  </Typography>
                ) : null}
              </Box>
              {headerExtra}
            </Box>
            {subtitle ? (
              <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8", fontWeight: 500, mt: 0.2 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: scrollable ? "auto" : "hidden",
            display: "flex",
            flexDirection: "column",
            ...(scrollable ? scrollSx : {}),
          }}
        >
          {children}
        </Box>
      </CardContent>
    </Card>
    );
  };

  const EmptyTileState = ({ message = "No data available", fill = false }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flex: fill ? 1 : undefined,
        width: fill ? "100%" : undefined,
        height: fill ? "100%" : undefined,
        minHeight: fill ? 80 : 64,
        borderRadius: "8px",
        bgcolor: "#f8fafc",
        border: "1px dashed #e2e8f0",
        px: 1,
      }}
    >
      <Typography sx={{ fontSize: "0.7rem", color: "#64748b", textAlign: "center" }}>
        {message}
      </Typography>
    </Box>
  );

  const statusChipSx = (kind) => {
    if (kind === "out") {
      return { bgcolor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" };
    }
    if (kind === "available") {
      return { bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" };
    }
    return { bgcolor: "#fff7ed", color: "#ea580c", border: "1px solid #fed7aa" };
  };

  const showPathologyTile =
    hasPathPackageAccess &&
    (isPathUser ||
      (!isPharmacyUser &&
        (userRoleNorm === "admin" ||
          userRoleNorm === "superadmin" ||
          userRoleNorm === "super_admin")));

  // Don't block rendering on error - show error as a warning but still render dashboard
  // if (error) {
  //   return (
  //     <Box sx={{ p: 3, textAlign: 'center' }}>
  //       <Typography variant="h6" color="error" gutterBottom>
  //         {error}
  //       </Typography>
  //       <Button variant="contained" onClick={fetchDashboardData}>
  //         Retry
  //       </Button>
  //     </Box>
  //   );
  // }

  return (
    <Box
      ref={dashboardRef}
      sx={{
        background: "#f5f7fb",
        mt: { md: -1.5 },
        height: { md: "calc(100vh - 88px)" },
        minHeight: { xs: "100%", md: "calc(100vh - 88px)" },
        maxHeight: { md: "calc(100vh - 88px)" },
        overflow: { md: "hidden" },
        p: { xs: 0.75, md: 0.85 },
        display: "flex",
        flexDirection: "column",
        gap: 0.6,
        boxSizing: "border-box",
      }}
    >
      {error && (
        <Box sx={{ p: 1, backgroundColor: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px", flexShrink: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.72rem", color: "#92400e" }}>
              {error}. Showing cached/fallback data.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setError(null);
                fetchDashboardData();
              }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {/* Compact header */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "8px",
          px: 1.1,
          py: 0.55,
          border: cardBorder,
          boxShadow: cardShadow,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 0.2 }}>
              <greeting.Icon sx={{ fontSize: 12, color: "#d97706" }} />
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#d97706" }}>
                {greeting.text}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "#0f172a", lineHeight: 1.15 }}>
              Hospital Management Dashboard
              {loading && (
                <Box component="span" sx={{ ml: 1, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <CircularProgress size={11} sx={{ color: "#2563eb" }} />
                  <Typography component="span" sx={{ fontSize: "0.6rem", color: "#64748b", fontWeight: 500 }}>
                    Refreshing...
                  </Typography>
                </Box>
              )}
            </Typography>
            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 600, mt: 0.15 }}>
              Overview of your hospital's performance and operations
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
              <Select
                value={monthSelectValue}
                onChange={handleMonthsChange}
                input={<OutlinedInput />}
                renderValue={(selected) =>
                  selected === "__all__"
                    ? formatFinancialYearLabel(selectedYear)
                    : `${monthNames[Number(selected)]} ${selectedYear}`
                }
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "6px",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  height: 30,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                }}
              >
                <MenuItem value="__all__" dense>
                  <ListItemText
                    primary={formatFinancialYearLabel(selectedYear)}
                    primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 700 }}
                  />
                </MenuItem>
                {monthNames.map((month, index) => (
                  <MenuItem key={index} value={index} dense>
                    <ListItemText
                      primary={`${month} ${selectedYears.length === 1 ? selectedYears[0] : ""}`.trim()}
                      primaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" sx={{ minWidth: 168 }}>
              <Select
                value={yearSelectValue}
                onChange={handleYearsChange}
                input={<OutlinedInput />}
                renderValue={(selected) => formatFinancialYearLabel(selected)}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "6px",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  height: 30,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                }}
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                  <MenuItem key={year} value={year} dense>
                    <ListItemText
                      primary={formatFinancialYearLabel(year)}
                      primaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              onClick={openDownloadMenu}
              sx={{
                color: "#334155",
                borderColor: "#e2e8f0",
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 30,
                bgcolor: "#fff",
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              <Download sx={{ mr: 0.5, fontSize: 15 }} />
              Download
            </Button>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={closeDownloadMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                dense
                onClick={() => {
                  closeDownloadMenu();
                  handleDownloadPdf("selected");
                }}
              >
                Download Selected ({selectionLabel})
              </MenuItem>
              <MenuItem
                dense
                onClick={() => {
                  closeDownloadMenu();
                  handleDownloadPdf("all");
                }}
              >
                Download All ({formatFinancialYearLabel(selectedYear)})
              </MenuItem>
            </Menu>
            <IconButton
              size="small"
              onClick={() => {
                console.log(`Manual refresh - months: ${selectedMonths.join(",")}, years: ${selectedYears.join(",")}`);
                patientSummaryCacheRef.current.clear();
                appointmentSummaryCacheRef.current.clear();
                clearAppointmentsListCache();
                fetchDashboardData();
                fetchIncomeExpensesData(selectedYear);
                fetchPeriodDependentAggregates();
                fetchInventoryStatusData();
                fetchPathInventoryStatusData();
                fetchAvailableDoctorsData();
                fetchSubscriptionPackage();
                fetchAllDepartments();
              }}
              sx={{
                color: "#2563eb",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                width: 30,
                height: 30,
                "&:hover": { backgroundColor: "#eff6ff" },
              }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* KPI row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            md: "repeat(5, minmax(0, 1fr))",
          },
          gap: 0.72,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <MetricCard
          title="Total Patients"
          icon={<LocalHospital />}
          color="#2563eb"
          monthLabel={kpiMonthLabel}
          monthValue={Number(patientSummaryData.totalPatients || 0).toLocaleString()}
          ytdValue={Number(patientYtdData.totalPatients || 0).toLocaleString()}
          trend={`${patientSummaryData.totalTrend === "down" ? "" : "+"}${patientSummaryData.totalChange || "0%"}`}
          trendUp={patientSummaryData.totalTrend !== "down"}
          trendHint={financialMetrics.priorMonthHint || undefined}
          footerLabel="New registrations"
          footerValue={Number(patientSummaryData.newPatients || 0).toLocaleString()}
          footerNote={
            isPremiumPackage
              ? `Total discharged (${monthNames[selectedMonth]}): ${Number(
                  patientSummaryData.dischargedPatients || 0
                ).toLocaleString()}`
              : undefined
          }
        />
        <MetricCard
          title="Total Income (₹)"
          icon={<AttachMoney />}
          color="#7c3aed"
          monthLabel={kpiMonthLabel}
          monthValue={financialMetrics.fmt(financialMetrics.monthIncome)}
          ytdValue={financialMetrics.fmt(financialMetrics.ytdIncome)}
          trend={financialMetrics.incomeTrend.change}
          trendUp={financialMetrics.incomeTrend.trendUp}
          trendHint={financialMetrics.priorMonthHint || undefined}
          sparklineData={incomeExpensesData.map((d) => d.income)}
          footerLabel="Avg/day"
          footerValue={financialMetrics.fmt(financialMetrics.avgIncome)}
        />
        <MetricCard
          title="Total Expense (₹)"
          icon={<ReceiptLong />}
          color="#dc2626"
          monthLabel={kpiMonthLabel}
          monthValue={financialMetrics.fmt(financialMetrics.monthExpense)}
          ytdValue={financialMetrics.fmt(financialMetrics.ytdExpense)}
          trend={financialMetrics.expenseTrend.change}
          trendUp={financialMetrics.expenseTrend.trendUp}
          trendHint={financialMetrics.priorMonthHint || undefined}
          sparklineData={incomeExpensesData.map((d) => d.expenses)}
          footerLabel="Avg/day"
          footerValue={financialMetrics.fmt(financialMetrics.avgExpense)}
        />
        <MetricCard
          title="Net Income (₹)"
          icon={<Savings />}
          color="#16a34a"
          monthLabel={kpiMonthLabel}
          monthValue={financialMetrics.fmt(financialMetrics.monthNet)}
          ytdValue={financialMetrics.fmt(financialMetrics.ytdNet)}
          trend={financialMetrics.netTrend.change}
          trendUp={financialMetrics.netTrend.trendUp}
          trendHint={financialMetrics.priorMonthHint || undefined}
          sparklineData={incomeExpensesData.map((d) => (d.income || 0) - (d.expenses || 0))}
          footerLabel="Avg/day"
          footerValue={financialMetrics.fmt(financialMetrics.avgNet)}
        />
        <QuickSummaryCard
          items={[
            {
              label: "New Patients",
              value: Number(patientSummaryData.newPatients || 0).toLocaleString(),
              color: "#2563eb",
              hint: `YTD ${Number(patientYtdData.newPatients || 0).toLocaleString()}`,
            },
            {
              label: "Follow Ups",
              value: Number(patientSummaryData.oldPatients || 0).toLocaleString(),
              color: "#16a34a",
              hint: `YTD ${Number(patientYtdData.oldPatients || 0).toLocaleString()}`,
            },
            { label: "Departments", value: scopedTotalDepartments },
            { label: "Doctors", value: dashboardData?.totalDoctors || 0 },
            { label: "Staff", value: dashboardData?.totalStaff || 0 },
            { label: "Medicine Items", value: dashboardData?.totalMedicines || 0 },
          ]}
        />
      </Box>

      {/* Row 2: Income vs Expenses | Department Revenue */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.5fr 1fr" },
          gap: 0.75,
          minHeight: { xs: 198, md: 216 },
          alignItems: "stretch",
        }}
      >
        <ChartCard
          title="Income vs Expenses"
          subtitle={formatFinancialYearLabel(selectedYear)}
          icon={<Typography sx={{ fontWeight: 800 }}>₹</Typography>}
          iconColor="#2563eb"
          height="100%"
          headerExtra={<IncomeExpensesChartLegend />}
        >
          <IncomeExpensesChart
            data={incomeExpensesData}
            height="100%"
            currentMonth={monthNamesShort[new Date().getMonth()]}
          />
        </ChartCard>

        <ChartCard
          title="Department Revenue"
          subtitle={periodSubtitle}
          icon={<DonutLarge />}
          iconColor="#7c3aed"
          height="100%"
        >
          <Box sx={{ flex: 1, minHeight: 128, height: "100%" }}>
            <IncomePerDepartmentChart
              data={departmentSummaryIncomeData}
              patientsByDepartment={departmentSummaryPatientsData}
            />
          </Box>
        </ChartCard>
      </Box>

      {/* Row 3: Medicine Inventory | Gender Wise Patients | Patients by Department | Appointment Summary */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 0.6,
          flexShrink: 0,
          minWidth: 0,
          height: { md: 210 },
          maxHeight: { md: 210 },
        }}
      >
        {!isPathUser && (
          <ChartCard title="Medicine Inventory Status" height="100%">
            <StatusTable
              rows={inventoryStatusData}
              columns={[
                {
                  key: "name",
                  header: "Medicine",
                  render: (medicine) => (
                    <Typography sx={{ fontWeight: 600, color: "#0f172a", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "inherit" }}>
                      {medicine.name}
                    </Typography>
                  ),
                },
                {
                  key: "quantity",
                  header: "Stock",
                  align: "right",
                  render: (medicine) => (
                    <Typography sx={{ fontWeight: 700, color: medicine.quantity === 0 ? "#dc2626" : "#ea580c", whiteSpace: "nowrap", fontSize: "inherit" }}>
                      {medicine.quantity} u
                    </Typography>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  align: "center",
                  render: (medicine) => (
                    <Chip
                      label={medicine.status || (medicine.quantity === 0 ? "OOS" : "Low")}
                      size="small"
                      sx={{ height: 16, fontSize: "0.52rem", fontWeight: 700, ...statusChipSx(medicine.quantity === 0 ? "out" : "low") }}
                    />
                  ),
                },
              ]}
            />
          </ChartCard>
        )}

        <ChartCard
          title="Gender Wise Patients"
          subtitle={`By gender · ${periodSubtitle}`}
          icon={<Groups />}
          iconColor="#7c3aed"
          height="100%"
        >
          <DonutLegendCard
            rows={[
              {
                name: "Female",
                value: departmentSummaryGenderData.reduce((sum, d) => sum + (d.female || 0), 0),
                color: "#db2777",
              },
              {
                name: "Male",
                value: departmentSummaryGenderData.reduce((sum, d) => sum + (d.male || 0), 0),
                color: "#2563eb",
              },
              {
                name: "Other",
                value: departmentSummaryGenderData.reduce((sum, d) => sum + (d.other || 0), 0),
                color: "#94a3b8",
              },
            ]}
            formatValue={(v) => Number(v || 0).toLocaleString()}
            centerLabel="Total"
            valueColHeader="Patients"
          />
        </ChartCard>

        <ChartCard
          title="Patients by Department"
          icon={<Apartment />}
          iconColor="#2563eb"
          height="100%"
        >
          <RankedListCard
            nameLabel="Department"
            countLabel="Patients"
            rows={departmentSummaryPatientsData.map((dept, index) => {
              const total = departmentSummaryPatientsData.reduce(
                (sum, d) => sum + (d.patients ?? d.count ?? d.patientCount ?? 0),
                0
              );
              const count = dept.patients ?? dept.count ?? dept.patientCount ?? 0;
              return {
                name: dept.department || dept.name || `Department ${index + 1}`,
                count,
                pct: total > 0 ? (count / total) * 100 : 0,
                color: TOP_TESTS_COLORS[index % TOP_TESTS_COLORS.length],
              };
            })}
          />
        </ChartCard>

        <ChartCard title="Appointment Summary" subtitle={periodSubtitle} height="100%">
          <DonutLegendCard
            rows={[
              { name: "Completed", value: appointmentStatusSummary.completed || 0, color: "#16a34a" },
              { name: "Cancelled", value: appointmentStatusSummary.cancelled || 0, color: "#dc2626" },
              { name: "No Show", value: appointmentStatusSummary.noShow || 0, color: "#ea580c" },
            ]}
            formatValue={(v) => Number(v || 0).toLocaleString()}
            centerLabel="Appointments"
            valueColHeader="Count"
          />
        </ChartCard>
      </Box>
    </Box>
  );
};

export default NewDashboard;

